/**
 * Unified Stream Processor
 *
 * Core stream processing engine that manages the full UIMessage[] conversation.
 * Single source of truth for message state.
 *
 * Handles:
 * - Full conversation management (UIMessage[])
 * - Text content accumulation with configurable chunking strategies
 * - Parallel tool calls with lifecycle state tracking
 * - Tool results and approval flows
 * - Thinking/reasoning content
 * - Recording/replay for testing
 * - Event-driven architecture for UI updates
 * - Per-message stream state tracking for multi-message sessions
 *
 * @see docs/chat-architecture.md — Canonical reference for AG-UI chunk ordering,
 *   adapter contract, single-shot flows, and expected UIMessage output.
 */
import {
  aguiContentToContentParts,
  aguiSnapshotMessageToUIMessage,
  coerceCreatedAt,
  generateMessageId,
  uiMessageToModelMessages,
} from '../messages.js'
import { runErrorEventToError } from '../../../utilities/errors'
import {
  isAssistantSegmentOf,
  isProviderExecutedToolCall,
} from '../../../utilities/provider-executed'
import {
  mergeMetadata,
  tanstackMetadata,
  withTanstackMetadata,
} from '../../../utilities/merge-metadata'
import { getChunkRunId } from '../../../utilities/chunk-ids'
import type { AdapterYieldChunk } from '../../../utilities/adapter-yield-chunk'
import {
  isContentPartArray,
  isToolResultOutcome,
  normalizeToolResult,
  toolResultErrorText,
} from '../../../utilities/tool-result'
import { defaultJSONParser } from './json-parser'
import {
  appendStructuredOutputDelta,
  completeStructuredOutputPart,
  errorStructuredOutputPart,
  updateTextPart,
  updateThinkingPart,
  updateToolCallApproval,
  updateToolCallApprovalResponse,
  updateToolCallPart,
  updateToolCallWithOutput,
  updateToolResultPart,
} from './message-updaters'
import { ImmediateStrategy } from './strategies'
import { INTERRUPT_BINDING_METADATA_KEY } from '../../../interrupt-resume'
import { EventType } from '../../../types'
import { splitSubagentWire } from '../../../utilities/subagent-wire'
import type { SubagentWireGroup } from '../../../utilities/subagent-wire'
import type { Message as AGUIMessage } from '@ag-ui/core'
import type {
  ChunkRecording,
  ChunkStrategy,
  InternalToolCallState,
  MessageStreamState,
  ProcessorResult,
  ProcessorState,
  ToolCallState,
  ToolResultState,
} from './types'
import {
  applyActivityDeltaToUIMessages,
  applyActivitySnapshotToUIMessages,
} from '../activity-records'
import type {
  ContentPart,
  Interrupt,
  MessagePart,
  ModelMessage,
  StreamChunk,
  ThinkingPart,
  ToolCall,
  ToolCallPart,
  ToolResultPart,
  SubagentPart,
  UIMessage,
  UIResourceEvent,
  UIResourcePart,
} from '../../../types'

/**
 * Events emitted by the StreamProcessor
 */
export interface StreamProcessorEvents {
  // State events - full array on any change
  onMessagesChange?: (messages: Array<UIMessage>) => void

  // Lifecycle events
  onStreamStart?: () => void
  onStreamEnd?: (message: UIMessage) => void
  onError?: (error: Error) => void

  // Interaction events - client must handle these
  onToolCall?: (args: {
    toolCallId: string
    toolName: string
    input: any
  }) => void
  onApprovalRequest?: (args: {
    toolCallId: string
    toolName: string
    input: any
    approvalId: string
  }) => void

  // Custom events from server-side tools
  onCustomEvent?: (
    eventType: string,
    data: unknown,
    context: { toolCallId?: string },
  ) => void

  // Granular events for UI optimization (character-by-character, state tracking)
  onTextUpdate?: (messageId: string, content: string) => void
  onToolCallStateChange?: (
    messageId: string,
    toolCallId: string,
    state: ToolCallState,
    args: string,
  ) => void
  onThinkingUpdate?: (
    messageId: string,
    stepId: string,
    content: string,
  ) => void
  onStructuredOutputChange?: (args: {
    phase: 'start' | 'update' | 'complete' | 'error'
    messageId: string
    status: 'streaming' | 'complete' | 'error'
    raw: string
    partial?: unknown
    data?: unknown
    reasoning?: string
    errorMessage?: string
    delta?: string
  }) => void
}

/**
 * Options for StreamProcessor
 */
export interface StreamProcessorOptions {
  chunkStrategy?: ChunkStrategy
  /** Event-driven handlers */
  events?: StreamProcessorEvents
  jsonParser?: {
    parse: (jsonString: string) => any
  }
  /** Enable recording for replay testing */
  recording?: boolean
  /** Initial messages to populate the processor */
  initialMessages?: Array<UIMessage>
  /**
   * Set on the processor of a subagent card. Chunks tagged with this id are
   * the card's own; chunks for an id no card holds are dropped.
   */
  subagentRunId?: string
}

const STRUCTURED_OUTPUT_UPDATE_BATCH_SIZE = 12

type ChunkFamily = 'TEXT_MESSAGE' | 'TOOL_CALL' | 'REASONING_MESSAGE'

/**
 * Events that leave this processor's open *_CHUNK stream open.
 * RAW, activity events, and REASONING_ENCRYPTED_VALUE match the AG-UI client.
 * Subagent lifecycle events stay here too: the child processor owns that
 * lane, and SUBAGENT_FINISHED or SUBAGENT_ERROR finalizes the child.
 */
const CHUNK_PASS_THROUGH: ReadonlySet<string> = new Set([
  'RAW',
  'ACTIVITY_SNAPSHOT',
  'ACTIVITY_DELTA',
  'REASONING_ENCRYPTED_VALUE',
  'SUBAGENT_STARTED',
  'SUBAGENT_FINISHED',
  'SUBAGENT_ERROR',
])

/** A run-level event closes every open shorthand lane, including children. */
const RUN_LEVEL_CLOSES_LANES: ReadonlySet<string> = new Set([
  'RUN_STARTED',
  'RUN_FINISHED',
  'RUN_ERROR',
  'MESSAGES_SNAPSHOT',
])

function interruptBatchHasGeneric(interrupts: Array<Interrupt>): boolean {
  return interrupts.some((interrupt) => {
    const metadata = interrupt.metadata
    if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
      return false
    }
    const binding = metadata[INTERRUPT_BINDING_METADATA_KEY]
    return (
      binding !== null &&
      typeof binding === 'object' &&
      !Array.isArray(binding) &&
      binding.kind === 'generic'
    )
  })
}

/**
 * The canonical arguments string for a `TOOL_CALL_END.input`, or `undefined`
 * when JSON cannot carry it: `JSON.stringify` returns `undefined` (despite
 * its declared type) for a top-level function or symbol and throws on BigInt
 * and circular references. `null` is not tool arguments either, so it also
 * keeps the streamed value instead of writing `arguments = "null"`.
 */
function serializeToolInput(input: unknown): string | undefined {
  if (input === undefined || input === null) return undefined
  try {
    const serialized: unknown = JSON.stringify(input)
    return typeof serialized === 'string' ? serialized : undefined
  } catch {
    return undefined
  }
}

/**
 * The input of a tool call from its raw arguments: `{}` for an empty string,
 * `undefined` when the arguments are not complete JSON (for example cut off)
 * or are `null`.
 */
function parseToolArguments(args: string): unknown {
  if (args.trim() === '') return {}
  try {
    const parsed: unknown = JSON.parse(args)
    return parsed ?? undefined
  } catch {
    return undefined
  }
}

/**
 * StreamProcessor - State machine for processing AI response streams
 *
 * Manages the full UIMessage[] conversation and emits events on changes.
 * Trusts the adapter contract: adapters emit clean AG-UI events in the
 * correct order.
 *
 * State tracking:
 * - Full message array
 * - Per-message stream state (text, tool calls, thinking)
 * - Multiple concurrent message streams
 * - Tool call completion via TOOL_CALL_END events
 *
 * @see docs/chat-architecture.md#streamprocessor-internal-state — State field reference
 * @see docs/chat-architecture.md#adapter-contract — What this class expects from adapters
 */
export class StreamProcessor {
  private readonly chunkStrategy: ChunkStrategy
  private readonly events: StreamProcessorEvents
  private readonly jsonParser: { parse: (jsonString: string) => any }
  private recordingEnabled: boolean

  // Message state
  private messages: Array<UIMessage> = []

  // Per-message stream state
  private readonly messageStates: Map<string, MessageStreamState> = new Map()
  private readonly activeMessageIds: Set<string> = new Set()
  private readonly toolCallToMessage: Map<string, string> = new Map()
  private pendingManualMessageId: string | null = null
  // True while the pending message came from startAssistantMessage(). Text
  // that names another id still goes into it.
  private legacyManualMessage = false
  private pendingThinkingStepId: string | null = null
  // The stream a run of *_CHUNK events is building. See expandChunk().
  private openChunk: { family: ChunkFamily; id: string } | null = null

  private readonly structuredMessageIds: Set<string> = new Set()
  private readonly structuredOutputUpdateBatches = new Map<
    string,
    {
      delta: string
      chunkCount: number
    }
  >()

  // Run tracking (for concurrent run safety)
  private readonly activeRuns = new Set<string>()
  // The messages each model call (run) wrote to, so its terminal event tags
  // only them. Messages written with no known run wait in the unassigned set.
  private readonly callMessageIds = new Map<string, Set<string>>()
  private readonly unassignedCallMessageIds = new Set<string>()
  // Tool calls started since a RUN_STARTED that found no other run active, in
  // order. A success RUN_FINISHED hands the unanswered ones to the client.
  private runToolCallIds: Array<string> = []
  // Tool calls that an interrupt or tool-input-available already handed to
  // onToolCall since that same reset, so the success path does not run them
  // twice.
  private readonly toolCallsSentToClient = new Set<string>()
  // Direct children by subagentRunId. See childProcessor().
  private readonly childProcessors = new Map<string, StreamProcessor>()
  // Child tool calls whose later events may omit subagentRunId.
  private readonly childToolCalls = new Map<string, string>()

  // Shared stream state
  private finishReason: string | null = null
  private hasError = false
  private isDone = false
  private streamEndEmitted = false

  // Recording
  private recording: ChunkRecording | null = null
  private recordingStartTime = 0

  private readonly subagentRunId?: string

  constructor(options: StreamProcessorOptions = {}) {
    this.subagentRunId = options.subagentRunId
    this.chunkStrategy = options.chunkStrategy || new ImmediateStrategy()
    this.events = options.events || {}
    this.jsonParser = options.jsonParser || defaultJSONParser
    this.recordingEnabled = options.recording ?? false

    // Initialize with provided messages
    if (options.initialMessages) {
      this.messages = [...options.initialMessages]
    }
  }

  // ============================================
  // Message Management Methods
  // ============================================

  /**
   * Set the messages array (e.g., from persisted state)
   */
  setMessages(messages: Array<UIMessage>): void {
    this.messages = [...messages]
    this.emitMessagesChange()
  }

  /**
   * Put older UI messages at the front of the conversation.
   *
   * Skip a message if its id is already in the list. Keep the existing message.
   * Then emit the same messages-change event as `setMessages`.
   *
   * Use this for older history pages. The first hydrate window uses `setMessages`.
   *
   * @param messages Older UI messages in insertion order. The first item is the oldest.
   *
   * @example
   * ```ts
   * processor.setMessages([newest])
   * processor.prependMessages([oldest])
   * ```
   */
  prependMessages(messages: Array<UIMessage>) {
    const existingIds = new Set(this.messages.map((message) => message.id))
    const olderMessages: Array<UIMessage> = []
    for (const message of messages) {
      const isDuplicate = existingIds.has(message.id)
      if (isDuplicate) {
        continue
      }
      existingIds.add(message.id)
      olderMessages.push(message)
    }
    this.messages = [...olderMessages, ...this.messages]
    this.emitMessagesChange()
  }

  /**
   * Add a user message to the conversation.
   * Supports both simple string content and multimodal content arrays.
   *
   * @param content - The message content (string or array of content parts)
   * @param id - Optional custom message ID (generated if not provided)
   * @param metadata - Optional AG-UI metadata bag
   * @returns The created UIMessage
   *
   * @example
   * ```ts
   * // Simple text message
   * processor.addUserMessage('Hello!')
   *
   * // Multimodal message with image
   * processor.addUserMessage([
   *   { type: 'text', content: 'What is in this image?' },
   *   { type: 'image', source: { type: 'url', value: 'https://example.com/photo.jpg' } }
   * ])
   *
   * // With custom ID
   * processor.addUserMessage('Hello!', 'custom-id-123')
   * ```
   */
  addUserMessage(
    content: string | Array<ContentPart>,
    id?: string,
    metadata?: UIMessage['metadata'],
  ): UIMessage {
    // Convert content to message parts
    const parts: Array<MessagePart> =
      typeof content === 'string'
        ? [{ type: 'text', content }]
        : content.map((part) => {
            // ContentPart types (text, image, audio, video, document) are compatible with MessagePart
            return part
          })

    const userMessage: UIMessage = {
      id: id ?? generateMessageId(),
      role: 'user',
      parts,
      createdAt: new Date(),
      ...(metadata != null ? { metadata } : {}),
    }

    this.messages = [...this.messages, userMessage]
    this.emitMessagesChange()

    return userMessage
  }

  /**
   * Prepare for a new assistant message stream.
   * Does NOT create the message immediately -- the message is created lazily
   * when the first content-bearing chunk arrives via ensureAssistantMessage().
   * This prevents empty assistant messages from flickering in the UI when
   * auto-continuation produces no content.
   */
  prepareAssistantMessage(): void {
    // Reset stream state for new message
    this.resetStreamState()
  }

  /**
   * @deprecated Use prepareAssistantMessage() instead. This eagerly creates
   * an assistant message which can cause empty message flicker.
   */
  startAssistantMessage(messageId?: string): string {
    this.prepareAssistantMessage()
    const { messageId: id } = this.ensureAssistantMessage(messageId)
    this.pendingManualMessageId = id
    this.legacyManualMessage = true
    return id
  }

  /**
   * Get the current assistant message ID (if one has been created).
   * Returns null if prepareAssistantMessage() was called but no content
   * has arrived yet.
   */
  getCurrentAssistantMessageId(): string | null {
    // Scan all message states (not just active) for the last assistant.
    // After finalizeStream() clears activeMessageIds, messageStates retains entries.
    // After reset() / resetStreamState(), messageStates is cleared → returns null.
    let lastId: string | null = null
    for (const [id, state] of this.messageStates) {
      if (state.role === 'assistant') {
        lastId = id
      }
    }
    return lastId
  }

  /**
   * Add a tool result (called by client after handling onToolCall)
   */
  addToolResult(toolCallId: string, output: any, error?: string): void {
    // Find the message containing this tool call
    const messageWithToolCall = this.messages.find((msg) =>
      msg.parts.some(
        (p): p is ToolCallPart => p.type === 'tool-call' && p.id === toolCallId,
      ),
    )

    if (!messageWithToolCall) {
      const owner = this.childOwningToolCall((part) => part.id === toolCallId)
      if (owner !== undefined) {
        this.updateChild(owner, (child) =>
          child.addToolResult(toolCallId, output, error),
        )
        return
      }
      console.warn(
        `[StreamProcessor] Could not find message with tool call ${toolCallId}`,
      )
      return
    }

    // Step 1: Update the tool-call part's output field (for UI rendering)
    let updatedMessages = updateToolCallWithOutput(
      this.messages,
      toolCallId,
      output,
      error ? 'error' : undefined,
      error,
    )

    // Step 2: Create a tool-result part (for LLM conversation history)
    const content = normalizeToolResult(output)
    const toolResultState: ToolResultState = error ? 'error' : 'complete'

    updatedMessages = updateToolResultPart(
      updatedMessages,
      messageWithToolCall.id,
      toolCallId,
      content,
      toolResultState,
      error,
    )

    this.messages = updatedMessages
    this.emitMessagesChange()
  }

  /**
   * Add an approval response (called by client after handling onApprovalRequest)
   */
  addToolApprovalResponse(approvalId: string, approved: boolean): void {
    const owner = this.childOwningToolCall(
      (part) => part.approval?.id === approvalId,
    )
    if (owner !== undefined) {
      this.updateChild(owner, (child) =>
        child.addToolApprovalResponse(approvalId, approved),
      )
      return
    }
    this.messages = updateToolCallApprovalResponse(
      this.messages,
      approvalId,
      approved,
    )
    this.emitMessagesChange()
  }

  /**
   * Get the conversation as ModelMessages (for sending to LLM)
   */
  toModelMessages(): Array<ModelMessage> {
    const modelMessages: Array<ModelMessage> = []
    for (const msg of this.messages) {
      modelMessages.push(...uiMessageToModelMessages(msg))
    }
    return modelMessages
  }

  /**
   * Get current messages
   */
  getMessages(): Array<UIMessage> {
    return this.messages
  }

  /**
   * Check if all tool calls in the last assistant message are complete
   * Useful for auto-continue logic
   */
  areAllToolsComplete(): boolean {
    const lastAssistant = this.messages.findLast(
      (m: UIMessage) => m.role === 'assistant',
    )

    if (!lastAssistant) return true

    const toolParts = lastAssistant.parts.filter(
      (p): p is ToolCallPart => p.type === 'tool-call',
    )

    if (toolParts.length === 0) return true

    // Get tool result parts to check for server tool completion
    const toolResultIds = new Set(
      lastAssistant.parts
        .filter((p): p is ToolResultPart => p.type === 'tool-result')
        .map((p) => p.toolCallId),
    )

    // All tool calls must be in a terminal state
    // A tool call is complete if:
    // 1. It was approved/denied (approval-responded state)
    // 2. It has an output field set (client tool completed via addToolResult)
    // 3. It has a corresponding tool-result part (server tool completed)
    // 4. It is provider-executed (e.g. Anthropic web_search) — already run by
    //    the provider, so there is no client result to wait for.
    return toolParts.every(
      (part) =>
        part.state === 'complete' ||
        part.state === 'approval-responded' ||
        (part.output !== undefined && !part.approval) ||
        toolResultIds.has(part.id) ||
        isProviderExecutedToolCall(part),
    )
  }

  /**
   * Remove messages after a certain index (for reload/retry)
   */
  removeMessagesAfter(index: number): void {
    const keptIds = new Set(this.messages.slice(0, index + 1).map((m) => m.id))
    // Drop routing state for messages that no longer exist; otherwise a
    // resumed stream (`reload()` or a server that reuses messageIds across
    // runs) could land deltas / tool args on stale map entries and corrupt
    // the new assistant message's parts. Mirror the four routing maps that
    // key on messageId: structuredMessageIds (custom-event routing),
    // messageStates (per-message stream state), toolCallToMessage (tool
    // args → message), and activeMessageIds (finalize / completeAllToolCalls
    // iteration targets — must not include phantoms).
    for (const id of this.structuredMessageIds) {
      if (!keptIds.has(id)) this.structuredMessageIds.delete(id)
    }
    for (const id of this.structuredOutputUpdateBatches.keys()) {
      if (!keptIds.has(id)) this.structuredOutputUpdateBatches.delete(id)
    }
    for (const id of this.messageStates.keys()) {
      if (!keptIds.has(id)) this.messageStates.delete(id)
    }
    for (const [toolCallId, msgId] of this.toolCallToMessage) {
      if (!keptIds.has(msgId)) this.toolCallToMessage.delete(toolCallId)
    }
    for (const id of this.activeMessageIds) {
      if (!keptIds.has(id)) this.activeMessageIds.delete(id)
    }
    if (
      this.pendingManualMessageId &&
      !keptIds.has(this.pendingManualMessageId)
    ) {
      this.pendingManualMessageId = null
      this.legacyManualMessage = false
    }
    for (const ids of this.callMessageIds.values()) {
      for (const id of ids) if (!keptIds.has(id)) ids.delete(id)
    }
    for (const id of this.unassignedCallMessageIds) {
      if (!keptIds.has(id)) this.unassignedCallMessageIds.delete(id)
    }
    this.messages = this.messages.slice(0, index + 1)
    this.emitMessagesChange()
  }

  /**
   * Clear all messages
   */
  clearMessages(): void {
    // Keep the run keys: a late terminal event of a known run then tags none.
    for (const ids of this.callMessageIds.values()) ids.clear()
    this.unassignedCallMessageIds.clear()
    this.messages = []
    this.messageStates.clear()
    this.activeMessageIds.clear()
    this.toolCallToMessage.clear()
    this.structuredMessageIds.clear()
    this.structuredOutputUpdateBatches.clear()
    this.pendingManualMessageId = null
    this.legacyManualMessage = false
    this.openChunk = null
    this.childProcessors.clear()
    this.childToolCalls.clear()
    this.emitMessagesChange()
  }

  // ============================================
  // Stream Processing Methods
  // ============================================

  /**
   * Process a stream and emit events through handlers
   */
  async process(stream: AsyncIterable<any>): Promise<ProcessorResult> {
    // Reset stream state (but keep messages)
    this.resetStreamState()

    // Start recording if enabled
    if (this.recordingEnabled) {
      this.startRecording()
    }

    // Process each chunk
    for await (const chunk of stream) {
      this.processChunk(chunk)
    }

    // Stream ended - finalize everything
    this.finalizeStream()

    // Finalize recording
    if (this.recording) {
      this.recording.result = this.getResult()
    }

    return this.getResult()
  }

  /**
   * Process a single chunk from the stream.
   *
   * Central dispatch for all AG-UI events. Each event type maps to a specific
   * handler. Events not listed in the switch are intentionally ignored
   * (STATE_SNAPSHOT, STATE_DELTA).
   *
   * @see docs/chat-architecture.md#adapter-contract — Expected event types and ordering
   */
  processChunk(chunk: StreamChunk): void {
    // Record chunk if enabled
    if (this.recording) {
      this.recording.chunks.push({
        chunk,
        timestamp: Date.now(),
        index: this.recording.chunks.length,
      })
    }

    if (this.routeToChild(chunk)) return

    for (const event of this.expandChunk(chunk)) this.dispatchChunk(event)
  }

  /** Send one event, after expandChunk(), to its handler. */
  private dispatchChunk(chunk: StreamChunk): void {
    const c = chunk
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- AG-UI EventType enum members vs string-literal case labels; default branch handles untraced events.
    switch (c.type) {
      // AG-UI Events
      case 'SUBAGENT_STARTED':
        this.handleSubagentStartedEvent(chunk)
        break

      case 'SUBAGENT_FINISHED':
        this.handleSubagentFinishedEvent(chunk)
        break

      case 'SUBAGENT_ERROR':
        this.handleSubagentErrorEvent(chunk)
        break

      case 'TEXT_MESSAGE_START':
        this.handleTextMessageStartEvent(
          chunk as Extract<StreamChunk, { type: 'TEXT_MESSAGE_START' }>,
        )
        break

      case 'TEXT_MESSAGE_CONTENT':
        this.handleTextMessageContentEvent(
          chunk as Extract<StreamChunk, { type: 'TEXT_MESSAGE_CONTENT' }>,
        )
        break

      case 'TEXT_MESSAGE_END':
        this.handleTextMessageEndEvent(
          chunk as Extract<StreamChunk, { type: 'TEXT_MESSAGE_END' }>,
        )
        break

      case 'TOOL_CALL_START':
        this.handleToolCallStartEvent(
          chunk as Extract<StreamChunk, { type: 'TOOL_CALL_START' }>,
        )
        break

      case 'TOOL_CALL_ARGS':
        this.handleToolCallArgsEvent(
          chunk as Extract<StreamChunk, { type: 'TOOL_CALL_ARGS' }>,
        )
        break

      case 'TOOL_CALL_END':
        this.handleToolCallEndEvent(
          chunk as Extract<StreamChunk, { type: 'TOOL_CALL_END' }>,
        )
        break

      case 'RUN_FINISHED':
        this.handleRunFinishedEvent(
          chunk as Extract<StreamChunk, { type: 'RUN_FINISHED' }>,
        )
        break

      case 'RUN_ERROR':
        this.handleRunErrorEvent(
          chunk as Extract<StreamChunk, { type: 'RUN_ERROR' }>,
        )
        break

      case 'STEP_FINISHED':
        this.handleStepFinishedEvent(
          chunk as Extract<StreamChunk, { type: 'STEP_FINISHED' }>,
        )
        break

      case 'MESSAGES_SNAPSHOT':
        this.handleMessagesSnapshotEvent(
          chunk as Extract<StreamChunk, { type: 'MESSAGES_SNAPSHOT' }>,
        )
        break

      case 'CUSTOM':
        this.handleCustomEvent(
          chunk as Extract<StreamChunk, { type: 'CUSTOM' }>,
        )
        break

      case 'RUN_STARTED':
        this.handleRunStartedEvent(
          chunk as Extract<StreamChunk, { type: 'RUN_STARTED' }>,
        )
        break

      case 'REASONING_START':
      case 'REASONING_MESSAGE_START':
      case 'REASONING_MESSAGE_END':
      case 'REASONING_END':
        // No special handling needed
        break

      case 'REASONING_MESSAGE_CONTENT':
        this.handleReasoningMessageContentEvent(
          chunk as Extract<StreamChunk, { type: 'REASONING_MESSAGE_CONTENT' }>,
        )
        break

      case 'REASONING_ENCRYPTED_VALUE':
        this.handleReasoningEncryptedValueEvent(
          chunk as Extract<StreamChunk, { type: 'REASONING_ENCRYPTED_VALUE' }>,
        )
        break

      case 'TOOL_CALL_RESULT':
        this.handleToolCallResultEvent(
          chunk as Extract<StreamChunk, { type: 'TOOL_CALL_RESULT' }>,
        )
        break

      case 'STEP_STARTED':
        this.handleStepStartedEvent(
          chunk as Extract<StreamChunk, { type: 'STEP_STARTED' }>,
        )
        break

      case 'ACTIVITY_SNAPSHOT':
        this.handleActivitySnapshotEvent(
          chunk as Extract<StreamChunk, { type: 'ACTIVITY_SNAPSHOT' }>,
        )
        break

      case 'ACTIVITY_DELTA':
        this.handleActivityDeltaEvent(
          chunk as Extract<StreamChunk, { type: 'ACTIVITY_DELTA' }>,
        )
        break

      default:
        // STATE_SNAPSHOT, STATE_DELTA - no special handling needed
        break
    }
  }

  /**
   * AG-UI lets a producer send one TEXT_MESSAGE_CHUNK, TOOL_CALL_CHUNK or
   * REASONING_MESSAGE_CHUNK in place of the START / CONTENT (ARGS) / END
   * events. A chunk with a new id opens a stream. A chunk with the same id or
   * no id continues it. Any other event closes it, except the ones in
   * CHUNK_PASS_THROUGH. Expand the shorthand into the START / CONTENT (ARGS)
   * / END events, so both forms build the same message.
   */
  private expandChunk(chunk: StreamChunk): Array<StreamChunk> {
    const open = this.openChunk
    const timestamp = chunk.timestamp
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- AG-UI EventType enum members vs string-literal case labels; default branch handles untraced events.
    switch (chunk.type) {
      case 'TEXT_MESSAGE_CHUNK': {
        const id = this.chunkStreamId(
          'TEXT_MESSAGE',
          chunk.messageId,
          chunk.type,
        )
        if (id === undefined) return this.closeChunk()
        const events: Array<StreamChunk> = []
        if (open?.family !== 'TEXT_MESSAGE' || open.id !== id) {
          events.push(...this.closeChunk(), {
            type: EventType.TEXT_MESSAGE_START,
            messageId: id,
            role: chunk.role ?? 'assistant',
            ...(chunk.name !== undefined ? { name: chunk.name } : {}),
            timestamp,
          })
          this.openChunk = { family: 'TEXT_MESSAGE', id }
        }
        if (chunk.delta !== undefined || chunk.metadata !== undefined) {
          events.push({
            type: EventType.TEXT_MESSAGE_CONTENT,
            messageId: id,
            delta: chunk.delta ?? '',
            metadata: chunk.metadata,
            timestamp,
          })
        }
        return events
      }
      case 'TOOL_CALL_CHUNK': {
        const id = this.chunkStreamId('TOOL_CALL', chunk.toolCallId, chunk.type)
        if (id === undefined) return this.closeChunk()
        const events: Array<StreamChunk> = []
        if (open?.family !== 'TOOL_CALL' || open.id !== id) {
          if (chunk.toolCallName === undefined) {
            console.warn(
              `[StreamProcessor] Dropped TOOL_CALL_CHUNK ${id}: the first chunk of a tool call needs a toolCallName`,
            )
            // Close the open stream, so a later chunk with no id cannot
            // continue it.
            return this.closeChunk()
          }
          events.push(...this.closeChunk(), {
            type: EventType.TOOL_CALL_START,
            toolCallId: id,
            toolCallName: chunk.toolCallName,
            parentMessageId: chunk.parentMessageId,
            metadata: chunk.metadata,
            timestamp,
          })
          this.openChunk = { family: 'TOOL_CALL', id }
        }
        if (chunk.delta !== undefined || chunk.metadata !== undefined) {
          events.push({
            type: EventType.TOOL_CALL_ARGS,
            toolCallId: id,
            delta: chunk.delta ?? '',
            ...(chunk.metadata !== undefined
              ? { metadata: chunk.metadata }
              : {}),
            timestamp,
          })
        }
        return events
      }
      case 'REASONING_MESSAGE_CHUNK': {
        const id = this.chunkStreamId(
          'REASONING_MESSAGE',
          chunk.messageId,
          chunk.type,
        )
        if (id === undefined) return this.closeChunk()
        const events: Array<StreamChunk> = []
        if (open?.family !== 'REASONING_MESSAGE' || open.id !== id) {
          events.push(...this.closeChunk(), {
            type: EventType.REASONING_MESSAGE_START,
            messageId: id,
            role: 'reasoning',
            timestamp,
          })
          this.openChunk = { family: 'REASONING_MESSAGE', id }
        }
        if (chunk.delta !== undefined || chunk.metadata !== undefined) {
          events.push({
            type: EventType.REASONING_MESSAGE_CONTENT,
            messageId: id,
            delta: chunk.delta ?? '',
            ...(chunk.metadata !== undefined
              ? { metadata: chunk.metadata }
              : {}),
            timestamp,
          })
        }
        return events
      }
      default: {
        const events =
          open && !CHUNK_PASS_THROUGH.has(chunk.type) ? this.closeChunk() : []
        if (RUN_LEVEL_CLOSES_LANES.has(chunk.type)) {
          this.closeChildChunkLanes()
        }
        return [...events, chunk]
      }
    }
  }

  /** Synthesize *_END on every nested shorthand lane and apply those events there. */
  private closeChildChunkLanes(): void {
    for (const child of this.childProcessors.values()) {
      for (const event of child.closeChunk()) child.dispatchChunk(event)
      child.closeChildChunkLanes()
    }
  }

  /**
   * The child an untagged chunk with no id continues: the only child whose
   * subtree has an open chunk stream of its kind, when this processor has
   * none. The AG-UI client resolves a chunk that tags only its opener the
   * same way.
   */
  private childContinuing(chunk: StreamChunk): string | undefined {
    let family: ChunkFamily
    if (chunk.type === 'TEXT_MESSAGE_CHUNK' && chunk.messageId === undefined) {
      family = 'TEXT_MESSAGE'
    } else if (
      chunk.type === 'REASONING_MESSAGE_CHUNK' &&
      chunk.messageId === undefined
    ) {
      family = 'REASONING_MESSAGE'
    } else if (
      chunk.type === 'TOOL_CALL_CHUNK' &&
      chunk.toolCallId === undefined
    ) {
      family = 'TOOL_CALL'
    } else {
      return undefined
    }
    if (this.openChunkCount(family) !== 1) return undefined
    for (const [id, child] of this.childProcessors) {
      if (child.openChunkCount(family) === 1) return id
    }
    return undefined
  }

  /**
   * How many open chunk streams of a kind this processor and all its nested
   * children hold.
   */
  private openChunkCount(family: ChunkFamily): number {
    let count = this.openChunk?.family === family ? 1 : 0
    for (const child of this.childProcessors.values()) {
      count += child.openChunkCount(family)
    }
    return count
  }

  /**
   * The id a chunk opens or continues. A chunk with no id continues. If it
   * continues nothing, the caller drops it and closes the open stream, so a
   * later chunk with no id cannot continue that stream either.
   */
  private chunkStreamId(
    family: ChunkFamily,
    id: string | undefined,
    type: string,
  ): string | undefined {
    if (id !== undefined) return id
    if (this.openChunk?.family === family) return this.openChunk.id
    console.warn(
      `[StreamProcessor] Dropped ${type}: it has no id and continues nothing`,
    )
    return undefined
  }

  private closeChunk(): Array<StreamChunk> {
    const open = this.openChunk
    if (!open) return []
    this.openChunk = null
    const timestamp = Date.now()
    switch (open.family) {
      case 'TEXT_MESSAGE':
        return [
          { type: EventType.TEXT_MESSAGE_END, messageId: open.id, timestamp },
        ]
      case 'TOOL_CALL':
        return [
          { type: EventType.TOOL_CALL_END, toolCallId: open.id, timestamp },
        ]
      case 'REASONING_MESSAGE':
        return [
          {
            type: EventType.REASONING_MESSAGE_END,
            messageId: open.id,
            timestamp,
          },
        ]
    }
  }

  // ============================================
  // Per-Message State Helpers
  // ============================================

  /**
   * Create a new MessageStreamState for a message
   */
  private createMessageState(
    messageId: string,
    role: 'user' | 'assistant' | 'system',
  ): MessageStreamState {
    const state: MessageStreamState = {
      id: messageId,
      role,
      totalTextContent: '',
      currentSegmentText: '',
      lastEmittedText: '',
      hasSeenReasoningEvents: false,
      thinkingSteps: new Map(),
      thinkingStepSignatures: new Map(),
      thinkingStepOrder: [],
      currentThinkingStepId: null,
      toolCalls: new Map(),
      toolCallOrder: [],
      hasToolCallsSinceTextStart: false,
      isComplete: false,
    }
    this.messageStates.set(messageId, state)
    return state
  }

  /**
   * Get the MessageStreamState for a message
   */
  private getMessageState(messageId: string): MessageStreamState | undefined {
    return this.messageStates.get(messageId)
  }

  /**
   * Promote a pending stepId from a STEP_STARTED that fired before the
   * assistant message existed onto the given message state, so the next
   * thinking event (STEP_FINISHED or REASONING_MESSAGE_CONTENT) attributes
   * to the correct step.
   */
  private consumePendingThinkingStep(state: MessageStreamState): void {
    if (!this.pendingThinkingStepId) return
    const stepId = this.pendingThinkingStepId
    state.currentThinkingStepId = stepId
    if (!state.thinkingSteps.has(stepId)) {
      state.thinkingSteps.set(stepId, '')
      state.thinkingStepOrder.push(stepId)
    }
    this.pendingThinkingStepId = null
  }

  /**
   * Get the most recent active assistant message ID.
   * Used as fallback for events that don't include a messageId.
   */
  private getActiveAssistantMessageId(): string | null {
    // While a run is active, only a message of that run (or the open
    // placeholder) takes events that name no message.
    const belongsToCurrentCall = (id: string) =>
      this.activeRuns.size === 0 ||
      [...this.activeRuns].some((runId) =>
        this.callMessageIds.get(runId)?.has(id),
      ) ||
      (this.pendingManualMessageId === id &&
        !this.messageStates.get(id)?.isComplete &&
        !this.isDone)
    // Set iteration is insertion-order; reverse-iterate to search from the end
    const ids = Array.from(this.activeMessageIds).reverse()
    for (const id of ids) {
      const state = this.messageStates.get(id)
      if (state && state.role === 'assistant' && belongsToCurrentCall(id)) {
        return id
      }
    }
    // finalizeStream() clears activeMessageIds but keeps messageStates.
    // Leftover reasoning after an early RUN_FINISHED must resume that
    // assistant. A new user turn calls prepareAssistantMessage(), which
    // clears messageStates first.
    for (const [id, state] of [...this.messageStates].reverse()) {
      if (state.role === 'assistant' && belongsToCurrentCall(id)) {
        return id
      }
    }
    return null
  }

  private resumeAssistantState(id: string, state: MessageStreamState): void {
    this.activeMessageIds.add(id)
    if (state.isComplete || this.isDone) {
      state.isComplete = false
      this.isDone = false
    }
  }

  /**
   * Ensure an active assistant message exists, creating one if needed.
   * Used for backward compat when events arrive without prior TEXT_MESSAGE_START.
   *
   * On reconnect/resume, a TEXT_MESSAGE_CONTENT may arrive for a message that
   * already exists in this.messages (e.g. from initialMessages or a prior
   * MESSAGES_SNAPSHOT) but whose transient state was cleared. In that case we
   * hydrate state from the existing message rather than creating a duplicate.
   */
  private ensureAssistantMessage(
    preferredId?: string,
    // False when `preferredId` names its own message (a tool call's
    // `parentMessageId`), so a different active message never takes it.
    fallbackToActive = true,
  ): {
    messageId: string
    state: MessageStreamState
  } {
    // Try to find state by preferred ID
    if (preferredId) {
      // The open placeholder of this call takes the first id the server sends.
      const pendingId = this.pendingManualMessageId
      if (
        pendingId &&
        pendingId !== preferredId &&
        !this.legacyManualMessage &&
        this.messageStates.get(pendingId)?.isComplete === false &&
        !this.messages.some((message) => message.id === preferredId)
      ) {
        this.renameMessage(pendingId, preferredId)
        this.pendingManualMessageId = preferredId
      }
      const state = this.getMessageState(preferredId)
      if (state) {
        this.resumeAssistantState(preferredId, state)
        return { messageId: preferredId, state }
      }
    }

    // Try active assistant message
    // An explicit id that is not the active message gets its own message.
    const activeId =
      fallbackToActive && preferredId === undefined
        ? this.getActiveAssistantMessageId()
        : null
    if (activeId) {
      const state = this.getMessageState(activeId)
      if (state) {
        this.resumeAssistantState(activeId, state)
        return { messageId: activeId, state }
      }
    }

    // Check if a message with preferredId already exists (reconnect/resume case).
    // Hydrate transient state from the existing message instead of duplicating it.
    // Activity messages occupy the same id space as text; do not steal their id.
    if (preferredId) {
      const existingMsg = this.messages.find((m) => m.id === preferredId)
      if (existingMsg?.role === 'activity') {
        preferredId = undefined
      } else if (existingMsg) {
        const state = this.createMessageState(preferredId, existingMsg.role)
        this.activeMessageIds.add(preferredId)

        // Seed segment text from the existing last text part so that
        // incoming deltas append correctly and updateTextPart produces
        // the right content (existing text + new delta).
        const lastPart =
          existingMsg.parts.length > 0
            ? existingMsg.parts[existingMsg.parts.length - 1]
            : null
        if (lastPart && lastPart.type === 'text') {
          state.currentSegmentText = lastPart.content
          state.lastEmittedText = lastPart.content
          state.totalTextContent = lastPart.content
        }

        return { messageId: preferredId, state }
      }
    }

    // Auto-create an assistant message (backward compat for process() without TEXT_MESSAGE_START)
    const id = preferredId || generateMessageId()
    const assistantMessage: UIMessage = {
      id,
      role: 'assistant',
      parts: [],
      createdAt: new Date(),
    }
    this.messages = [...this.messages, assistantMessage]
    const state = this.createMessageState(id, 'assistant')
    this.activeMessageIds.add(id)
    // Only a client id is a placeholder. A server id is already final.
    if (!preferredId) this.pendingManualMessageId = id
    this.events.onStreamStart?.()
    this.emitMessagesChange()
    return { messageId: id, state }
  }

  // ============================================
  // Event Handlers
  // ============================================

  /**
   * Merge event metadata onto a UIMessage. `tanstack` is deep-merged so a
   * later delta does not wipe `tanstack.model`. High-frequency leftover
   * keys (`content`, `args`) never stamp onto the message.
   * Rebuilds `createdAt` when `tanstack.createdAt` is an ISO string.
   */
  private mergeMessageMetadata(messageId: string, incoming: unknown): void {
    const runId =
      tanstackMetadata({
        metadata:
          incoming !== null && typeof incoming === 'object'
            ? { ...incoming }
            : undefined,
      })?.runId ??
      (this.activeRuns.size === 1 ? [...this.activeRuns][0] : undefined)
    if (runId !== undefined) {
      const ids = this.callMessageIds.get(runId) ?? new Set<string>()
      ids.add(messageId)
      this.callMessageIds.set(runId, ids)
    } else {
      this.unassignedCallMessageIds.add(messageId)
    }
    if (
      incoming == null ||
      typeof incoming !== 'object' ||
      Array.isArray(incoming)
    ) {
      return
    }
    const message = this.messages.find((msg) => msg.id === messageId)
    if (!message) return

    const incomingRecord = incoming as NonNullable<UIMessage['metadata']>
    const incomingTanstack = tanstackMetadata(incomingRecord)
    const toMerge =
      incomingTanstack != null &&
      ('content' in incomingTanstack || 'args' in incomingTanstack)
        ? {
            ...incomingRecord,
            tanstack: Object.fromEntries(
              Object.entries(incomingTanstack).filter(
                ([key]) => key !== 'content' && key !== 'args',
              ),
            ),
          }
        : incomingRecord
    const metadata = mergeMetadata(message.metadata, toMerge)
    const createdAt = coerceCreatedAt(
      tanstackMetadata(incomingRecord)?.createdAt,
    )
    const createdAtValid = createdAt !== undefined
    this.messages = this.messages.map((msg) =>
      msg.id === messageId
        ? {
            ...msg,
            ...(metadata !== undefined ? { metadata } : {}),
            ...(createdAtValid ? { createdAt } : {}),
          }
        : msg,
    )
    this.emitMessagesChange()
  }

  private findSubagentPart(subagentRunId: string) {
    for (const message of this.messages) {
      for (const part of message.parts) {
        if (part.type === 'subagent' && part.subagent.id === subagentRunId) {
          return { message, part }
        }
      }
    }
    return undefined
  }

  /**
   * The direct child whose card holds `id`: the child itself, or a nested
   * child inside its messages.
   */
  private childOwning(id: string): string | undefined {
    const holds = (messages: ReadonlyArray<UIMessage>): boolean =>
      messages.some((message) =>
        message.parts.some(
          (part) =>
            part.type === 'subagent' &&
            (part.subagent.id === id || holds(part.subagent.messages)),
        ),
      )
    for (const message of this.messages) {
      for (const part of message.parts) {
        if (part.type !== 'subagent') continue
        if (part.subagent.id === id || holds(part.subagent.messages)) {
          return part.subagent.id
        }
      }
    }
    return undefined
  }

  /** The direct child whose messages hold this tool call. */
  private childOwningToolCall(
    matches: (part: ToolCallPart) => boolean,
  ): string | undefined {
    const holds = (messages: ReadonlyArray<UIMessage>): boolean =>
      messages.some((message) =>
        message.parts.some(
          (part) =>
            (part.type === 'tool-call' && matches(part)) ||
            (part.type === 'subagent' && holds(part.subagent.messages)),
        ),
      )
    for (const message of this.messages) {
      for (const part of message.parts) {
        if (part.type === 'subagent' && holds(part.subagent.messages)) {
          return part.subagent.id
        }
      }
    }
    return undefined
  }

  /**
   * The processor for a direct child. The card's messages are the source of
   * truth. The processor re-reads them when something else replaced them.
   */
  private childProcessor(subagentRunId: string): StreamProcessor | undefined {
    const found = this.findSubagentPart(subagentRunId)
    if (!found) return undefined
    const messages = found.part.subagent.messages
    const existing = this.childProcessors.get(subagentRunId)
    if (existing) {
      if (existing.getMessages() !== messages) existing.messages = messages
      return existing
    }
    const child = new StreamProcessor({
      subagentRunId,
      initialMessages: messages,
      events: {
        onToolCall: (args) => this.events.onToolCall?.(args),
        onApprovalRequest: (args) => this.events.onApprovalRequest?.(args),
        onCustomEvent: (name, value, context) =>
          this.events.onCustomEvent?.(name, value, context),
      },
    })
    this.childProcessors.set(subagentRunId, child)
    return child
  }

  /** Run `update` on a direct child, then copy its messages onto the card. */
  private updateChild(
    subagentRunId: string,
    update: (child: StreamProcessor) => void,
  ): void {
    const child = this.childProcessor(subagentRunId)
    if (!child) return
    update(child)
    const messages = child.getMessages()
    this.patchSubagent(subagentRunId, (subagent) => {
      subagent.messages = messages
    })
  }

  private handleSubagentStartedEvent(chunk: StreamChunk): void {
    if (chunk.type !== 'SUBAGENT_STARTED') return
    const existing = this.findSubagentPart(chunk.subagentRunId)
    if (existing) {
      // A resume continues the same child. Start a fresh stream on its card.
      this.childProcessors.delete(chunk.subagentRunId)
      this.patchSubagent(chunk.subagentRunId, (subagent) => {
        subagent.status = 'running'
        delete subagent.interruptIds
        delete subagent.error
        if (chunk.metadata !== undefined) subagent.metadata = chunk.metadata
      })
      return
    }
    const { messageId } = this.ensureAssistantMessage()
    const part: SubagentPart = {
      type: 'subagent',
      subagent: {
        id: chunk.subagentRunId,
        name: chunk.name,
        ...(chunk.description !== undefined && {
          description: chunk.description,
        }),
        status: 'running',
        ...(chunk.parentSubagentRunId !== undefined && {
          parentSubagentRunId: chunk.parentSubagentRunId,
        }),
        ...(chunk.parentToolCallId !== undefined && {
          parentToolCallId: chunk.parentToolCallId,
        }),
        ...(chunk.metadata !== undefined && { metadata: chunk.metadata }),
        messages: [],
      },
    }
    this.messages = this.messages.map((message) =>
      message.id === messageId
        ? { ...message, parts: [...message.parts, part] }
        : message,
    )
    this.emitMessagesChange()
  }

  private handleSubagentFinishedEvent(chunk: StreamChunk): void {
    if (chunk.type !== 'SUBAGENT_FINISHED') return
    this.updateChild(chunk.subagentRunId, (child) => child.finalizeStream())
    this.patchSubagent(chunk.subagentRunId, (subagent) => {
      if (subagent.status === 'error') return
      if (chunk.outcome?.type === 'suspended') {
        subagent.status = 'suspended'
        subagent.interruptIds = chunk.outcome.interruptIds ?? []
        return
      }
      subagent.status = 'finished'
    })
  }

  private handleSubagentErrorEvent(chunk: StreamChunk): void {
    if (chunk.type !== 'SUBAGENT_ERROR') return
    this.updateChild(chunk.subagentRunId, (child) => child.finalizeStream())
    this.patchSubagent(chunk.subagentRunId, (subagent) => {
      subagent.status = 'error'
      subagent.error = {
        message: chunk.message,
        ...(chunk.code !== undefined && { code: chunk.code }),
      }
    })
  }

  private patchSubagent(
    subagentRunId: string,
    patch: (subagent: SubagentPart['subagent']) => void,
  ): void {
    this.messages = this.messages.map((message) => {
      const index = message.parts.findIndex(
        (part) =>
          part.type === 'subagent' && part.subagent.id === subagentRunId,
      )
      if (index === -1) return message
      const part = message.parts[index]
      if (!part || part.type !== 'subagent') return message
      const next: SubagentPart = {
        type: 'subagent',
        subagent: { ...part.subagent },
      }
      patch(next.subagent)
      const parts = [...message.parts]
      parts[index] = next
      return { ...message, parts }
    })
    this.emitMessagesChange()
  }

  /**
   * Send a child's chunk to that child's processor, so the card keeps text,
   * reasoning, tool calls, results, and nested children. A tool event without
   * `subagentRunId` follows its `TOOL_CALL_START` or `TOOL_CALL_CHUNK`. An
   * untagged chunk with no id follows a child's open chunk stream (see
   * childContinuing()).
   */
  private routeToChild(chunk: StreamChunk): boolean {
    let id: string | undefined
    if (chunk.type === 'SUBAGENT_STARTED') {
      id = chunk.parentSubagentRunId
    } else if (
      chunk.type === 'SUBAGENT_FINISHED' ||
      chunk.type === 'SUBAGENT_ERROR'
    ) {
      // A direct child's own lifecycle goes to dispatchChunk's switch, not to a
      // child.
      if (this.findSubagentPart(chunk.subagentRunId)) return false
      id = chunk.subagentRunId
    } else if ('subagentRunId' in chunk && chunk.subagentRunId) {
      id = chunk.subagentRunId
    } else if ('toolCallId' in chunk && typeof chunk.toolCallId === 'string') {
      id = this.childToolCalls.get(chunk.toolCallId)
    } else {
      id = this.childContinuing(chunk)
    }
    if (id === undefined) return false
    const owner = this.childOwning(id)
    if (owner === undefined) {
      // A card's own chunks reach its processor tagged with its id.
      if (id === this.subagentRunId) return false
      // No card holds this id. Keep the chunk off this message list.
      console.warn(
        `[StreamProcessor] Dropped a chunk for unknown subagent ${id}`,
      )
      return true
    }
    if (this.findSubagentPart(owner)?.part.subagent.status === 'error') {
      // An errored child, and its nested children, ignore late events.
      // Finished and suspended children still accept them, for a resume.
      return true
    }
    if (
      (chunk.type === 'TOOL_CALL_START' || chunk.type === 'TOOL_CALL_CHUNK') &&
      chunk.toolCallId !== undefined
    ) {
      this.childToolCalls.set(chunk.toolCallId, owner)
    }
    this.updateChild(owner, (child) => child.processChunk(chunk))
    return true
  }

  private renameMessage(fromId: string, toId: string): void {
    // Update the message's ID in the messages array
    this.messages = this.messages.map((msg) =>
      msg.id === fromId ? { ...msg, id: toId } : msg,
    )

    // Move state to the new key
    const existingState = this.messageStates.get(fromId)
    if (existingState) {
      existingState.id = toId
      this.messageStates.delete(fromId)
      this.messageStates.set(toId, existingState)
    }

    // Update activeMessageIds
    this.activeMessageIds.delete(fromId)
    this.activeMessageIds.add(toId)

    // TOOL_CALL_ARGS/END route through toolCallToMessage. Keep those
    // entries on the remapped id so later args still accumulate
    // (interleaved text can arrive as a full START/CONTENT/END block).
    for (const [toolCallId, mappedMessageId] of this.toolCallToMessage) {
      if (mappedMessageId === fromId) {
        this.toolCallToMessage.set(toolCallId, toId)
      }
    }

    // `structured-output.start` can arrive before this event and mark
    // the pending id. Keep the mark on the remapped id so the JSON deltas
    // go into the structured-output part, not a text part.
    if (this.structuredMessageIds.delete(fromId)) {
      this.structuredMessageIds.add(toId)
    }
    // Queued deltas move too, so the next flush of `toId` sends them.
    const batch = this.structuredOutputUpdateBatches.get(fromId)
    if (batch) {
      this.structuredOutputUpdateBatches.delete(fromId)
      this.structuredOutputUpdateBatches.set(toId, batch)
    }
    // The renamed message stays a member of its model call.
    for (const ids of this.callMessageIds.values()) {
      if (ids.delete(fromId)) ids.add(toId)
    }
    if (this.unassignedCallMessageIds.delete(fromId))
      this.unassignedCallMessageIds.add(toId)
  }

  /**
   * Handle TEXT_MESSAGE_START event
   */
  private handleTextMessageStartEvent(
    chunk: Extract<StreamChunk, { type: 'TEXT_MESSAGE_START' }>,
  ): void {
    const { messageId, role } = chunk
    if (
      this.messages.some((m) => m.id === messageId && m.role === 'activity')
    ) {
      console.warn(
        `TEXT_MESSAGE_START: Dropped text for '${messageId}', the id of an activity message`,
      )
      return
    }

    // Map 'tool' and 'developer' roles to 'assistant' for both UIMessage and MessageStreamState
    // (UIMessage doesn't support 'tool'/'developer' role, and lookups like
    // getActiveAssistantMessageId() check state.role === 'assistant')
    const uiRole: 'system' | 'user' | 'assistant' =
      role === 'user' || role === 'system' ? role : 'assistant'

    // Case 1: A manual message was created via startAssistantMessage()
    if (this.pendingManualMessageId) {
      const pendingId = this.pendingManualMessageId
      this.pendingManualMessageId = null
      this.legacyManualMessage = false

      if (pendingId !== messageId) {
        this.renameMessage(pendingId, messageId)
      }

      // Ensure state exists
      let pendingState = this.messageStates.get(messageId)
      if (!pendingState) {
        pendingState = this.createMessageState(messageId, uiRole)
        this.activeMessageIds.add(messageId)
      } else if (pendingState.hasToolCallsSinceTextStart) {
        // A tool call (e.g. TOOL_CALL_START with parentMessageId) marked
        // this message before its "real" TEXT_MESSAGE_START arrived — same
        // reset Case 2 performs, so the segment accumulator doesn't carry
        // stale tool-call state into the text that follows.
        if (pendingState.currentSegmentText !== pendingState.lastEmittedText) {
          this.emitTextUpdateForMessage(messageId)
        }
        pendingState.currentSegmentText = ''
        pendingState.lastEmittedText = ''
        pendingState.hasToolCallsSinceTextStart = false
      }

      this.applySenderName(messageId, chunk.name)
      this.mergeMessageMetadata(messageId, chunk.metadata)
      this.emitMessagesChange()
      return
    }

    // Case 2: Message already exists (dedup)
    const existingMsg = this.messages.find((m) => m.id === messageId)
    if (existingMsg) {
      this.activeMessageIds.add(messageId)
      const existingState = this.messageStates.get(messageId)
      if (!existingState) {
        this.createMessageState(messageId, uiRole)
      } else {
        // If tool calls happened since last text, this TEXT_MESSAGE_START
        // signals a new text segment — reset segment accumulation
        if (existingState.hasToolCallsSinceTextStart) {
          if (
            existingState.currentSegmentText !== existingState.lastEmittedText
          ) {
            this.emitTextUpdateForMessage(messageId)
          }
          existingState.currentSegmentText = ''
          existingState.lastEmittedText = ''
          existingState.hasToolCallsSinceTextStart = false
        }
      }
      this.applySenderName(messageId, chunk.name)
      this.mergeMessageMetadata(messageId, chunk.metadata)
      return
    }

    // Case 3: New message from the stream
    const newMessage: UIMessage = {
      id: messageId,
      role: uiRole,
      parts: [],
      createdAt: new Date(),
    }

    this.messages = [...this.messages, newMessage]
    this.createMessageState(messageId, uiRole)
    this.activeMessageIds.add(messageId)

    this.applySenderName(messageId, chunk.name)
    this.mergeMessageMetadata(messageId, chunk.metadata)
    this.events.onStreamStart?.()
    this.emitMessagesChange()
  }

  private applySenderName(messageId: string, name: string | undefined): void {
    if (name === undefined) return
    this.messages = this.messages.map((msg) =>
      msg.id === messageId ? { ...msg, name } : msg,
    )
  }

  /**
   * Handle TEXT_MESSAGE_END event
   */
  private handleTextMessageEndEvent(
    chunk: Extract<StreamChunk, { type: 'TEXT_MESSAGE_END' }>,
  ): void {
    const { messageId } = chunk
    this.mergeMessageMetadata(messageId, chunk.metadata)
    const state = this.getMessageState(messageId)
    if (!state) return
    if (state.isComplete) return

    // Emit any pending text for this message
    if (state.currentSegmentText !== state.lastEmittedText) {
      this.emitTextUpdateForMessage(messageId)
    }
  }

  /**
   * Handle MESSAGES_SNAPSHOT event
   */
  private handleMessagesSnapshotEvent(
    chunk: Extract<StreamChunk, { type: 'MESSAGES_SNAPSHOT' }>,
  ): void {
    this.resetStreamState()
    // Normalize AG-UI snapshot messages to UIMessage[] so every message has a
    // `parts` array. AG-UI messages carry `content` but no `parts`, so casting
    // them directly to UIMessage[] is unsafe and causes "Cannot read properties
    // of undefined (reading 'find')" when code later reads message.parts (e.g.
    // the onToolCallStateChange devtools handler).
    //
    // The AG-UI `MESSAGES_SNAPSHOT` wire shape cannot reconstruct client-side
    // tool-call metadata a server may omit: a `role: 'tool'` message only carries
    // `toolCallId` + `content`, and an assistant message in the snapshot may
    // drop `toolCalls` the client already observed via `TOOL_CALL_*` events.
    // Without the matching `tool-call` part, later `addToolResult(toolCallId)`
    // calls cannot locate the call and warn + no-op (see #859). To keep the
    // UI representation consistent with the streaming fan-out and preserve the
    // unreconstructable metadata, reconcile the normalized snapshot against
    // the pre-snapshot state; see `reconcileSnapshotToolCalls`.
    const prevMessages = this.messages
    const prevById = new Map(prevMessages.map((msg) => [msg.id, msg]))
    const { top, groups } = splitSubagentWire(chunk.messages)
    const normalized = this.mergeReasoningFanOut(
      top.map(aguiSnapshotMessageToUIMessage),
    )
    const reconciled = this.reconcileSnapshotToolCalls(
      normalized,
      prevMessages,
    ).map((msg) => {
      if (msg.metadata != null) return msg
      const prev = prevById.get(msg.id)
      if (prev?.metadata == null) return msg
      return { ...msg, metadata: prev.metadata }
    })
    this.messages = this.mergeOmittedActivity(
      prevMessages,
      this.attachSnapshotSubagents(reconciled, top, groups, prevMessages),
    )
    this.emitMessagesChange()
  }

  /**
   * Snapshot is authoritative for user/assistant/system. Activity omitted
   * from the snapshot (TanStack chat() still builds snapshots from
   * ModelMessage[]) stays in the transcript so persist/reconnect cannot
   * wipe it. Snapshot activity rows win when the id is present.
   *
   * Keep every snapshot row, including two assistants that share an id
   * (agent-loop retry reuses `currentMessageId`). A Map keyed by id would
   * drop the earlier error tool-call (#1192).
   */
  private mergeOmittedActivity(
    prevMessages: Array<UIMessage>,
    snapshot: Array<UIMessage>,
  ): Array<UIMessage> {
    const snapshotIds = new Set(snapshot.map((msg) => msg.id))
    const omitted = prevMessages.filter(
      (msg) => msg.role === 'activity' && !snapshotIds.has(msg.id),
    )
    if (omitted.length === 0) {
      return snapshot
    }
    const out = [...snapshot]
    for (const activity of omitted) {
      const prevIndex = prevMessages.findIndex((msg) => msg.id === activity.id)
      out.splice(Math.min(Math.max(prevIndex, 0), out.length), 0, activity)
    }
    return out
  }

  /**
   * Put subagent cards back on a snapshot. Child wire messages (tagged with
   * `subagentRunId`) become a card on the nearest assistant message before
   * them, or a new assistant message when there is none. A card that the
   * snapshot does not carry stays on its message, because a server snapshot
   * can hold the parent history only.
   */
  private attachSnapshotSubagents(
    messages: Array<UIMessage>,
    top: ReadonlyArray<AGUIMessage>,
    groups: ReadonlyArray<SubagentWireGroup<AGUIMessage>>,
    prevMessages: ReadonlyArray<UIMessage>,
  ): Array<UIMessage> {
    const cards: Array<{
      hostId?: string
      toolCallId?: string
      part: SubagentPart
      carried: boolean
    }> = []
    const children = new Map<string, StreamProcessor>()
    for (const group of groups) {
      const child = new StreamProcessor({ subagentRunId: group.id })
      child.processChunk({
        type: EventType.MESSAGES_SNAPSHOT,
        messages: group.messages,
        timestamp: Date.now(),
      })
      children.set(group.id, child)
      const { placeholder: _placeholder, ...info } = group.info
      void _placeholder
      cards.push({
        hostId: top
          .slice(0, group.hostIndex + 1)
          .findLast((message) => message.role === 'assistant')?.id,
        part: {
          type: 'subagent',
          subagent: { ...info, id: group.id, messages: child.getMessages() },
        },
        carried: false,
      })
    }
    const inSnapshot = new Set(groups.map((group) => group.id))
    for (const message of prevMessages) {
      for (const part of message.parts) {
        if (part.type !== 'subagent' || inSnapshot.has(part.subagent.id)) {
          continue
        }
        cards.push({
          hostId: message.id,
          ...(part.subagent.parentToolCallId !== undefined && {
            toolCallId: part.subagent.parentToolCallId,
          }),
          part,
          carried: true,
        })
        const kept = this.childProcessors.get(part.subagent.id)
        if (kept) children.set(part.subagent.id, kept)
      }
    }
    this.childProcessors.clear()
    for (const [id, child] of children) this.childProcessors.set(id, child)

    let out = messages
    for (const card of cards) {
      let index = out.findIndex((message) => message.id === card.hostId)
      if (index === -1 && card.toolCallId !== undefined) {
        index = out.findIndex((message) =>
          message.parts.some(
            (part) => part.type === 'tool-call' && part.id === card.toolCallId,
          ),
        )
      }
      if (index === -1 && !card.carried) {
        index = out.findLastIndex((message) => message.role === 'assistant')
      }
      if (index === -1) {
        if (!card.carried) {
          out = [
            ...out,
            { id: generateMessageId(), role: 'assistant', parts: [card.part] },
          ]
        }
        continue
      }
      out = out.map((message, position) =>
        position !== index ||
        message.parts.some(
          (part) =>
            part.type === 'subagent' &&
            part.subagent.id === card.part.subagent.id,
        )
          ? message
          : { ...message, parts: [...message.parts, card.part] },
      )
    }
    return out
  }

  /**
   * Reconcile a freshly normalized snapshot with the pre-snapshot message
   * state so unreconstructable tool-call metadata is preserved.
   *
   * Post-pass (a): anchor `tool-result`-only assistant messages (the shape
   * `aguiSnapshotMessageToUIMessage` emits for AG-UI `role: 'tool'` wire
   * messages) into the message containing the matching `tool-call` part, or —
   * when the snapshot supplies no such part — the nearest earlier anchorable
   * assistant message, matching the in-stream fan-out shape
   * `assistant: [text, tool-call, tool-result, ...]`. Detached messages with
   * no earlier anchorable assistant are kept verbatim.
   *
   * Post-pass (b): when a `tool-result` part references a `toolCallId` whose
   * `tool-call` part is absent from the snapshot, carry the `tool-call` part
   * forward from the pre-snapshot state (state and output untouched) so a
   * subsequent `addToolResult(toolCallId)` can still locate the call.
   *
   * Post-pass (c): AG-UI wire snapshots rebuild `tool-call` parts as
   * `input-complete` without `output` (ModelMessage has no result field on
   * the call). After anchoring results, copy each `tool-result` onto its
   * matching `tool-call` (and prefer pre-snapshot complete/output when the
   * snapshot is poorer) so server tools keep the same UI shape as client tools.
   */
  /**
   * Wire order is reasoning fan-outs, then the assistant anchor.
   * Snapshot conversion turns each reasoning row into its own assistant
   * message. Fold leading thinking-only messages into the next real
   * assistant. Do not fold into a tool-result-only message (`role: 'tool'`
   * on the wire). `reconcileSnapshotToolCalls` anchors those results.
   * A `<id>-segment-<n>` row folds into its message, together with the
   * tool-result rows between them (a tool result ends a wire segment).
   */
  private mergeReasoningFanOut(messages: Array<UIMessage>): Array<UIMessage> {
    const out: Array<UIMessage> = []
    let pending: Array<UIMessage> = []
    const thinkingParts = (msg: UIMessage) =>
      msg.parts.filter((part): part is ThinkingPart => part.type === 'thinking')
    const isThinkingOnly = (msg: UIMessage) =>
      msg.role === 'assistant' &&
      msg.parts.length > 0 &&
      msg.parts.every((part) => part.type === 'thinking')
    const isToolResultOnly = (msg: UIMessage) =>
      msg.role === 'assistant' &&
      msg.parts.some((part) => part.type === 'tool-result') &&
      msg.parts.every(
        (part) => part.type === 'tool-result' || part.type === 'ui-resource',
      )
    const flushPending = () => {
      out.push(...pending)
      pending = []
    }
    for (const msg of messages) {
      if (isThinkingOnly(msg)) {
        pending.push(msg)
        continue
      }
      let next = msg
      if (
        msg.role === 'assistant' &&
        pending.length > 0 &&
        !isToolResultOnly(msg)
      ) {
        next = {
          ...msg,
          parts: [...pending.flatMap(thinkingParts), ...msg.parts],
        }
        pending = []
      } else {
        flushPending()
      }
      // Look back past tool-result rows to the message this segment is of.
      let parentIndex = out.length - 1
      while (parentIndex >= 0) {
        const candidate = out[parentIndex]
        if (!candidate || !isToolResultOnly(candidate)) break
        parentIndex--
      }
      const parent = out[parentIndex]
      if (
        parent?.role === 'assistant' &&
        next.role === 'assistant' &&
        isAssistantSegmentOf(next.id, parent.id)
      ) {
        // The parent, the tool results after it, then the segment: row order.
        const folded = out
          .slice(parentIndex)
          .flatMap((message) => message.parts)
        out.splice(parentIndex, out.length - parentIndex, {
          ...parent,
          parts: [...folded, ...next.parts],
        })
        continue
      }
      out.push(next)
    }
    flushPending()
    return out
  }

  private reconcileSnapshotToolCalls(
    snapshot: Array<UIMessage>,
    prevMessages: Array<UIMessage>,
  ): Array<UIMessage> {
    // Index tool-call parts observed before the snapshot by id so we can
    // restore metadata the snapshot cannot re-emit. Duplicate ids resolve
    // last-write-wins: the same tool call can appear in multiple messages
    // across reconnects, and the most recent part carries the freshest state.
    const prevToolCalls = new Map<string, ToolCallPart>()
    for (const msg of prevMessages) {
      for (const part of msg.parts) {
        if (part.type === 'tool-call') {
          prevToolCalls.set(part.id, part)
        }
      }
    }
    // Index tool-call parts already present in the snapshot so (b) only fills
    // genuine gaps rather than duplicating a tool-call the snapshot supplies.
    const snapshotToolCallIds = new Set<string>()
    for (const msg of snapshot) {
      for (const part of msg.parts) {
        if (part.type === 'tool-call') {
          snapshotToolCallIds.add(part.id)
        }
      }
    }

    const reconciled: Array<UIMessage> = []
    for (const msg of snapshot) {
      const toolResultParts = msg.parts.filter(
        (part): part is ToolResultPart => part.type === 'tool-result',
      )
      const toolResultPart =
        msg.role === 'assistant' &&
        toolResultParts.length === 1 &&
        msg.parts.every(
          (part) => part.type === 'tool-result' || part.type === 'ui-resource',
        )
          ? toolResultParts[0]
          : undefined

      if (!toolResultPart) {
        reconciled.push(msg)
        continue
      }

      // Prefer the message that actually contains the matching tool-call
      // part. AG-UI `reasoning` messages normalize to `role: 'assistant'`,
      // so anchoring into the nearest assistant alone could separate a
      // result from its call (and a later `addToolResult(toolCallId)`
      // would then append a duplicate result next to the call).
      const target =
        reconciled.findLast((m) =>
          m.parts.some(
            (p) => p.type === 'tool-call' && p.id === toolResultPart.toolCallId,
          ),
        ) ??
        reconciled.findLast(
          (m) =>
            m.role === 'assistant' &&
            !(m.parts.length === 1 && m.parts[0]?.type === 'tool-result'),
        )

      if (!target) {
        // No assistant to anchor into — keep the detached message intact.
        if (!snapshotToolCallIds.has(toolResultPart.toolCallId)) {
          console.warn(
            `[StreamProcessor] MESSAGES_SNAPSHOT contains a tool-result for "${toolResultPart.toolCallId}" but no matching tool-call exists in the snapshot, and there is no assistant message to anchor into; addToolResult("${toolResultPart.toolCallId}") will not be able to locate this call`,
          )
        }
        reconciled.push(msg)
        continue
      }

      const parts = [...target.parts]
      // (b) Fill in a missing tool-call part from the pre-snapshot state when
      // the snapshot references its id via a tool-result but supplies no
      // tool-call metadata of its own.
      if (
        !snapshotToolCallIds.has(toolResultPart.toolCallId) &&
        !parts.some(
          (p) => p.type === 'tool-call' && p.id === toolResultPart.toolCallId,
        )
      ) {
        const prev = prevToolCalls.get(toolResultPart.toolCallId)
        if (prev) {
          // Insert the carried-over tool-call before its tool-result (pushed
          // below) so call→result ordering matches the streaming fan-out.
          parts.push({ ...prev })
          snapshotToolCallIds.add(prev.id)
        } else {
          console.warn(
            `[StreamProcessor] MESSAGES_SNAPSHOT contains a tool-result for "${toolResultPart.toolCallId}" but no matching tool-call exists in the snapshot or the pre-snapshot state; addToolResult("${toolResultPart.toolCallId}") will not be able to locate this call`,
          )
        }
      }
      parts.push(...msg.parts)
      // Replace rather than push into `target.parts`: a snapshot message that
      // arrived already carrying `parts` (TanStack server echoing UIMessages)
      // shares its array with the incoming chunk, and mutating it in place
      // would corrupt the caller's event object.
      target.parts = parts
    }

    return this.enrichSnapshotToolCallsFromResults(reconciled, prevToolCalls)
  }

  /**
   * Post-pass (c): fold `tool-result` content into sibling `tool-call` parts
   * and prefer pre-snapshot complete/output when the snapshot rebuilt a
   * poorer `input-complete` call (AG-UI ModelMessage has no result on calls).
   */
  private enrichSnapshotToolCallsFromResults(
    messages: Array<UIMessage>,
    prevToolCalls: Map<string, ToolCallPart>,
  ): Array<UIMessage> {
    const resultsByCallId = new Map<string, ToolResultPart>()
    for (const msg of messages) {
      for (const part of msg.parts) {
        if (part.type === 'tool-result') {
          resultsByCallId.set(part.toolCallId, part)
        }
      }
    }

    return messages.map((msg) => {
      const parts = msg.parts.map((part) => {
        if (part.type !== 'tool-call') return part

        const prev = prevToolCalls.get(part.id)
        const result = resultsByCallId.get(part.id)
        let next: ToolCallPart = part

        // Prefer a pre-snapshot call that already carried output/complete —
        // the client observed TOOL_CALL_END/RESULT before the snapshot wipe.
        if (
          prev &&
          (prev.output !== undefined ||
            prev.state === 'complete' ||
            prev.state === 'error') &&
          (part.output === undefined ||
            part.state === 'input-complete' ||
            part.state === 'input-streaming' ||
            part.state === 'awaiting-input')
        ) {
          next = {
            ...part,
            ...(prev.output !== undefined ? { output: prev.output } : {}),
            state: prev.state,
            ...(prev.approval !== undefined ? { approval: prev.approval } : {}),
            ...(prev.metadata !== undefined ? { metadata: prev.metadata } : {}),
          }
        }

        // Apply sibling tool-result when the call still has no output.
        if (result && next.output === undefined) {
          let output: unknown
          if (Array.isArray(result.content)) {
            output = result.content
          } else {
            try {
              output = JSON.parse(result.content)
            } catch {
              output = result.content
            }
          }
          const errorText =
            result.state === 'error' ? toolResultErrorText(output) : undefined
          next = {
            ...next,
            output: errorText ? { error: errorText } : output,
            state: result.state === 'error' ? 'error' : 'complete',
          }
        }

        return next
      })
      // Rebuild only when a part object identity changed.
      const partsChanged = parts.some(
        (part, index) => part !== msg.parts[index],
      )
      return partsChanged ? { ...msg, parts } : msg
    })
  }

  /**
   * Handle TEXT_MESSAGE_CONTENT event.
   *
   * Accumulates delta into both currentSegmentText (for UI emission) and
   * totalTextContent (for ProcessorResult). Lazily creates the assistant
   * UIMessage on first content. Uses updateTextPart() which replaces the
   * last TextPart or creates a new one depending on part ordering.
   *
   * @see docs/chat-architecture.md#single-shot-text-response — Text accumulation step-by-step
   * @see docs/chat-architecture.md#uimessage-part-ordering-invariants — Replace vs. push logic
   */
  private handleTextMessageContentEvent(
    chunk: Extract<StreamChunk, { type: 'TEXT_MESSAGE_CONTENT' }>,
  ): void {
    if (
      this.messages.some(
        (m) => m.id === chunk.messageId && m.role === 'activity',
      )
    ) {
      console.warn(
        `TEXT_MESSAGE_CONTENT: Dropped text for '${chunk.messageId}', the id of an activity message`,
      )
      return
    }
    const preferredId =
      this.legacyManualMessage && this.pendingManualMessageId
        ? this.pendingManualMessageId
        : chunk.messageId
    const { messageId, state } = this.ensureAssistantMessage(preferredId)
    this.mergeMessageMetadata(messageId, chunk.metadata)

    if (this.structuredMessageIds.has(messageId)) {
      const delta = chunk.delta || ''
      if (delta !== '') {
        this.messages = appendStructuredOutputDelta(
          this.messages,
          messageId,
          delta,
        )
        state.totalTextContent += delta
        this.queueStructuredOutputUpdate(messageId, delta)
        this.emitMessagesChange()
      }
      return
    }

    const previousSegment = state.currentSegmentText

    // Detect if this is a NEW text segment (after tool calls) vs continuation
    const isNewSegment =
      state.hasToolCallsSinceTextStart &&
      previousSegment.length > 0 &&
      this.isNewTextSegment(chunk, previousSegment)

    if (isNewSegment) {
      // Emit any accumulated text before starting new segment
      if (previousSegment !== state.lastEmittedText) {
        this.emitTextUpdateForMessage(messageId)
      }
      // Reset SEGMENT text accumulation for the new text segment after tool calls
      state.currentSegmentText = ''
      state.lastEmittedText = ''
      state.hasToolCallsSinceTextStart = false
    }

    // A segment begins at its first delta, not its second. Left set, the flag
    // makes the second delta look like a further segment: the accumulation
    // resets and updateTextPart writes over the part the first delta created.
    // A later tool call sets it again in handleToolCallStartEvent.
    state.hasToolCallsSinceTextStart = false

    const currentText = state.currentSegmentText
    const delta = chunk.delta || ''
    const nextText = delta !== '' ? currentText + delta : currentText

    // Calculate the delta for totalTextContent
    const textDelta = nextText.slice(currentText.length)
    state.currentSegmentText = nextText
    state.totalTextContent += textDelta

    const chunkPortion = chunk.delta || ''
    const shouldEmit = this.chunkStrategy.shouldEmit(
      chunkPortion,
      state.currentSegmentText,
    )
    if (shouldEmit && state.currentSegmentText !== state.lastEmittedText) {
      this.emitTextUpdateForMessage(messageId)
    }
  }

  /**
   * Handle TOOL_CALL_START event.
   *
   * Creates a new InternalToolCallState entry in the toolCalls Map and appends
   * a ToolCallPart to the UIMessage. Duplicate toolCallId is a no-op.
   *
   * CRITICAL: This MUST be received before any TOOL_CALL_ARGS for the same
   * toolCallId. Args for unknown IDs are silently dropped.
   *
   * @see docs/chat-architecture.md#single-shot-tool-call-response — Tool call state transitions
   * @see docs/chat-architecture.md#parallel-tool-calls-single-shot — Parallel tracking by ID
   * @see docs/chat-architecture.md#adapter-contract — Ordering requirements
   */
  private handleToolCallStartEvent(
    chunk: Extract<StreamChunk, { type: 'TOOL_CALL_START' }>,
  ): void {
    // A placeholder (e.g. made by thinking) takes the tool call's parent id.
    // Otherwise the next model call's TEXT_MESSAGE_START renames it, and one
    // message holds two calls under the wrong id.
    const pendingId = this.pendingManualMessageId
    const parentId = chunk.parentMessageId
    if (pendingId && parentId && parentId !== pendingId) {
      const parent = this.messages.find((m) => m.id === parentId)
      const placeholder = this.messages.find((m) => m.id === pendingId)
      if (!parent) {
        this.pendingManualMessageId = null
        this.renameMessage(pendingId, parentId)
      } else if (parent.role === 'assistant' && placeholder) {
        // The parent already exists. Fold the placeholder into it, so the
        // thinking stays in the same message as the tool call.
        this.pendingManualMessageId = null
        this.messages = this.messages
          .filter((m) => m.id !== pendingId)
          .map((m) =>
            m.id === parentId
              ? { ...m, parts: [...m.parts, ...placeholder.parts] }
              : m,
          )
        this.renameMessage(pendingId, parentId)
      }
    }

    // Determine the message this tool call belongs to
    const targetMessageId =
      chunk.parentMessageId ?? this.getActiveAssistantMessageId()
    const { messageId, state } = this.ensureAssistantMessage(
      targetMessageId ?? undefined,
      chunk.parentMessageId === undefined,
    )
    const tanstack = tanstackMetadata(chunk)
    this.mergeMessageMetadata(
      messageId,
      tanstack === undefined ? undefined : { tanstack },
    )

    // Mark that we've seen tool calls since the last text segment
    state.hasToolCallsSinceTextStart = true

    const toolCallId = chunk.toolCallId
    const existingToolCall = state.toolCalls.get(toolCallId)

    if (!existingToolCall) {
      // New tool call starting
      const initialState: ToolCallState = 'awaiting-input'

      const toolName = chunk.toolCallName

      // Capture provider metadata that arrived on TOOL_CALL_START so it
      // round-trips back through the assistant message on the next turn
      // (e.g. Gemini's thoughtSignature).
      const chunkMetadata = chunk.metadata

      const newToolCall: InternalToolCallState = {
        id: chunk.toolCallId,
        name: toolName,
        arguments: '',
        state: initialState,
        parsedArguments: undefined,
        index: state.toolCalls.size,
        ...(chunkMetadata !== undefined && { metadata: chunkMetadata }),
      }

      state.toolCalls.set(toolCallId, newToolCall)
      state.toolCallOrder.push(toolCallId)
      this.runToolCallIds.push(toolCallId)

      // Store mapping for TOOL_CALL_ARGS/END routing
      this.toolCallToMessage.set(toolCallId, messageId)

      // Update UIMessage
      this.messages = updateToolCallPart(this.messages, messageId, {
        id: chunk.toolCallId,
        name: toolName,
        arguments: '',
        state: initialState,
        ...(chunkMetadata !== undefined && { metadata: chunkMetadata }),
      })
      this.emitMessagesChange()

      // Emit granular event
      this.events.onToolCallStateChange?.(
        messageId,
        chunk.toolCallId,
        initialState,
        '',
      )
    }
  }

  /**
   * Handle TOOL_CALL_ARGS event.
   *
   * Appends the delta to the tool call's accumulated arguments string.
   * Transitions state from awaiting-input → input-streaming on first non-empty delta.
   * Attempts partial JSON parse on each update for UI preview.
   *
   * If toolCallId is not found in the Map (no preceding TOOL_CALL_START),
   * this event is silently dropped.
   *
   * @see docs/chat-architecture.md#single-shot-tool-call-response — Step-by-step tool call processing
   */
  private handleToolCallArgsEvent(
    chunk: Extract<StreamChunk, { type: 'TOOL_CALL_ARGS' }>,
  ): void {
    const toolCallId = chunk.toolCallId
    const messageId = this.toolCallToMessage.get(toolCallId)
    if (!messageId) return

    const state = this.getMessageState(messageId)
    if (!state) return

    const existingToolCall = state.toolCalls.get(toolCallId)
    if (!existingToolCall) return

    const wasAwaitingInput = existingToolCall.state === 'awaiting-input'

    if (chunk.metadata !== undefined) {
      existingToolCall.metadata = mergeMetadata(
        existingToolCall.metadata,
        chunk.metadata,
      )
    }

    // Accumulate arguments from delta
    existingToolCall.arguments += chunk.delta || ''

    // Update state
    if (wasAwaitingInput && chunk.delta) {
      existingToolCall.state = 'input-streaming'
    }

    // Try to parse the updated arguments
    existingToolCall.parsedArguments = this.jsonParser.parse(
      existingToolCall.arguments,
    )

    // Update UIMessage
    this.messages = updateToolCallPart(this.messages, messageId, {
      id: existingToolCall.id,
      name: existingToolCall.name,
      arguments: existingToolCall.arguments,
      state: existingToolCall.state,
      ...(existingToolCall.metadata !== undefined && {
        metadata: existingToolCall.metadata,
      }),
    })
    this.emitMessagesChange()

    // Emit granular event
    this.events.onToolCallStateChange?.(
      messageId,
      existingToolCall.id,
      existingToolCall.state,
      existingToolCall.arguments,
    )
  }

  /**
   * Handle TOOL_CALL_END event — arguments are finalized (input-complete).
   * Tool output arrives on TOOL_CALL_RESULT, not on this event.
   *
   * If TOOL_CALL_END carries parsed `input`, it is the canonical arguments:
   * write it into the accumulated string and override the rendered part's
   * `input` with it. That covers adapters that deliver the whole input on END
   * (e.g. Anthropic server_tool_use / web_search — issue #839) and adapters
   * that stream the wire arguments and then normalize them (OpenAI strict-mode
   * null widening, undone by the adapter after #939), so `arguments` and
   * `input` never disagree on the persisted part.
   *
   * @see docs/chat-architecture.md#single-shot-tool-call-response — End-to-end flow
   */
  private handleToolCallEndEvent(
    chunk: Extract<StreamChunk, { type: 'TOOL_CALL_END' }>,
  ): void {
    const messageId = this.toolCallToMessage.get(chunk.toolCallId)
    if (!messageId) return

    const msgState = this.getMessageState(messageId)
    if (!msgState) return

    // The parsed input can ride on the spec `input` field or, for adapters
    // that only stamp it into TanStack metadata, on `metadata.tanstack.input`.
    const input =
      chunk.input !== undefined
        ? chunk.input
        : (tanstackMetadata(chunk)?.input as unknown)

    // Transition the tool call to input-complete (the authoritative completion signal)
    const existingToolCall = msgState.toolCalls.get(chunk.toolCallId)
    if (existingToolCall && existingToolCall.state !== 'input-complete') {
      // The parsed input replaces the accumulated arguments string, so
      // completeToolCall's strict parse surfaces the canonical value on the
      // ToolCallPart even when the streamed deltas carried a provider-side
      // reshaping of it. An input JSON cannot carry is not canonical: both
      // `arguments` and `input` then stay with the streamed value, so the
      // two never disagree.
      const serializedInput = serializeToolInput(input)
      if (serializedInput !== undefined) {
        existingToolCall.arguments = serializedInput
      }

      const index = msgState.toolCallOrder.indexOf(chunk.toolCallId)
      this.completeToolCall(messageId, index, existingToolCall)

      // Canonicalize on the parsed input: overrides the accumulated-args parse
      // that completeToolCall wrote (adapters may coerce values differently
      // between streamed args and the final structured input).
      if (serializedInput !== undefined) {
        existingToolCall.parsedArguments = input
        this.messages = updateToolCallPart(this.messages, messageId, {
          id: existingToolCall.id,
          name: existingToolCall.name,
          arguments: existingToolCall.arguments,
          state: 'input-complete',
          input,
          ...(existingToolCall.metadata !== undefined && {
            metadata: existingToolCall.metadata,
          }),
        })
        this.emitMessagesChange()
      }
    }
  }

  /**
   * Handle TOOL_CALL_RESULT event (AG-UI spec).
   *
   * Creates a tool-result part and updates the tool-call output field,
   * mirroring the logic from TOOL_CALL_END when it carries a result.
   * This is the spec-compliant path for delivering tool results to the client.
   */
  private handleToolCallResultEvent(
    chunk: Extract<StreamChunk, { type: 'TOOL_CALL_RESULT' }>,
  ): void {
    // A resume stream delivers TOOL_CALL_RESULT for a tool call that was
    // started in a PRIOR run. A preceding MESSAGES_SNAPSHOT resets stream state
    // (clearing `toolCallToMessage`), so fall back to locating the message that
    // owns the tool-call part — matching `addToolResult`/`handleInterrupts`.
    // Without this the result is dropped, the follow-up request omits the tool
    // message, and the still-"pending" tool call re-interrupts (issue #532).
    const messageId =
      this.toolCallToMessage.get(chunk.toolCallId) ??
      this.messages.find((m) =>
        m.parts.some(
          (p): p is ToolCallPart =>
            p.type === 'tool-call' && p.id === chunk.toolCallId,
        ),
      )?.id
    if (!messageId) return

    const extra = chunk as AdapterYieldChunk
    const rawToolResultOutcome = tanstackMetadata(chunk)?.toolResultOutcome
    const toolResultOutcome = isToolResultOutcome(rawToolResultOutcome)
      ? rawToolResultOutcome
      : undefined
    const isOutputError =
      extra.state === 'output-error' ||
      tanstackMetadata(chunk)?.state === 'output-error' ||
      toolResultOutcome !== undefined

    // Step 1: Update the tool-call part's output field
    let output: unknown
    try {
      output =
        typeof chunk.content === 'string'
          ? JSON.parse(chunk.content)
          : chunk.content
    } catch {
      output = chunk.content
    }
    this.messages = updateToolCallWithOutput(
      this.messages,
      chunk.toolCallId,
      output,
      isOutputError ? 'error' : undefined,
    )

    // Step 2: Create/update the tool-result part
    const resultState: ToolResultState = isOutputError ? 'error' : 'complete'
    this.messages = updateToolResultPart(
      this.messages,
      messageId,
      chunk.toolCallId,
      // The server sends a ContentPart[] result as a JSON string. Keep the
      // parsed array so uiMessagesToWire can carry it in metadata.
      isContentPartArray(output)
        ? output
        : aguiContentToContentParts(chunk.content),
      resultState,
      resultState === 'error' ? toolResultErrorText(output) : undefined,
      toolResultOutcome,
    )
    this.emitMessagesChange()
  }

  /**
   * Handle RUN_STARTED event.
   *
   * Registers the run so that RUN_FINISHED can determine whether other
   * runs are still active before finalizing.
   */
  private handleRunStartedEvent(
    chunk: Extract<StreamChunk, { type: 'RUN_STARTED' }>,
  ): void {
    // A run that overlaps another keeps the calls both runs started.
    if (this.activeRuns.size === 0) {
      this.runToolCallIds = []
      this.toolCallsSentToClient.clear()
      // A new run owes its own stream end. A client that only watches never
      // resets between runs, so without this only its first run would end.
      this.streamEndEmitted = false
    }
    this.activeRuns.add(chunk.runId)
  }

  /**
   * Handle RUN_FINISHED event.
   *
   * Records the finishReason and removes the run from activeRuns.
   * Only finalizes when no more runs are active, so that concurrent
   * runs don't interfere with each other.
   *
   * @see docs/chat-architecture.md#single-shot-tool-call-response — finishReason semantics
   * @see docs/chat-architecture.md#adapter-contract — Why RUN_FINISHED is mandatory
   */
  private handleRunFinishedEvent(
    chunk: Extract<StreamChunk, { type: 'RUN_FINISHED' }>,
  ): void {
    const extra = chunk as AdapterYieldChunk
    // Tag the messages of this model call with its response identity.
    const incoming = tanstackMetadata(chunk)
    const model = chunk.model ?? incoming?.model
    const responseId = chunk.responseId ?? incoming?.responseId
    const ids =
      this.callMessageIds.get(chunk.runId) ??
      (this.activeRuns.has(chunk.runId)
        ? new Set<string>()
        : new Set(this.unassignedCallMessageIds))
    for (const messageId of ids) {
      this.mergeMessageMetadata(
        messageId,
        withTanstackMetadata(chunk, {
          ...(model !== undefined ? { model } : {}),
          ...(responseId !== undefined ? { responseId } : {}),
        }).metadata,
      )
    }
    if (this.pendingManualMessageId && ids.has(this.pendingManualMessageId)) {
      this.pendingManualMessageId = null
      this.legacyManualMessage = false
    }
    this.callMessageIds.delete(chunk.runId)
    for (const id of ids) this.unassignedCallMessageIds.delete(id)
    this.finishReason =
      extra.finishReason !== undefined
        ? extra.finishReason
        : (tanstackMetadata(chunk)?.finishReason ?? null)
    this.activeRuns.delete(chunk.runId)

    if (chunk.outcome?.type === 'interrupt') {
      this.handleInterrupts(chunk.outcome.interrupts)
    }

    if (this.activeRuns.size === 0) {
      this.completeAllToolCalls()
      const isIntermediateToolTurn =
        this.finishReason === 'tool_calls' &&
        chunk.outcome?.type !== 'interrupt'
      if (isIntermediateToolTurn) {
        return
      }
      if (!chunk.outcome || chunk.outcome.type === 'success') {
        this.handlePendingToolCalls(chunk.outcome?.pendingToolCallIds)
      }
      this.isDone = true
      this.finalizeStream()
    }
  }

  /**
   * AG-UI ends a run that calls a frontend tool with a success outcome and
   * leaves the call unanswered. The pending calls are the ones named in
   * `pendingToolCallIds` that this run started, or else every call this run
   * started that has no result. Fire onToolCall once for each, so the client
   * runs it and continues.
   */
  private handlePendingToolCalls(pendingToolCallIds?: Array<string>): void {
    const ids = pendingToolCallIds?.length
      ? pendingToolCallIds.filter((id) => this.runToolCallIds.includes(id))
      : this.runToolCallIds
    // Read the parts, not the stream state: a MESSAGES_SNAPSHOT before
    // RUN_FINISHED resets the stream state.
    const parts = this.messages
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- `parts` is typed as required, but seeded ModelMessage-shaped messages can lack it at runtime.
      .flatMap((msg) => msg.parts ?? [])
    for (const toolCallId of ids) {
      if (this.toolCallsSentToClient.has(toolCallId)) continue
      const part = parts.find(
        (p): p is ToolCallPart => p.type === 'tool-call' && p.id === toolCallId,
      )
      if (
        !part ||
        // The provider already ran it (e.g. Anthropic web_search).
        isProviderExecutedToolCall(part) ||
        // Answered, by the same rule as areAllToolsComplete().
        part.output !== undefined ||
        parts.some(
          (p) => p.type === 'tool-result' && p.toolCallId === toolCallId,
        ) ||
        this.isToolCallPartTerminal(toolCallId) ||
        this.isToolCallPartAwaitingUserAction(toolCallId)
      ) {
        continue
      }
      // A call of an answer cut at the output limit never runs, also when
      // its arguments parse. This is expected, so it logs nothing.
      if (this.finishReason === 'length') continue
      // A snapshot can carry a part with arguments but no input. Parse them
      // strictly, as completeToolCall() does. An empty argument string is a
      // call with no arguments.
      const input = part.input ?? parseToolArguments(part.arguments)
      if (input === undefined) {
        console.warn(
          `[StreamProcessor] Did not run tool call ${toolCallId}: the arguments of ${part.name} are not complete JSON`,
        )
        continue
      }
      this.toolCallsSentToClient.add(toolCallId)
      this.events.onToolCall?.({ toolCallId, toolName: part.name, input })
    }
  }

  /**
   * Apply interrupt state to tool-call parts, then fire the client events.
   * A child's interrupt updates the child's card. `emit` is false there, so
   * the events fire once, from the top processor.
   */
  private handleInterrupts(interrupts: Array<Interrupt>, emit = true): void {
    const hasGeneric = interruptBatchHasGeneric(interrupts)
    for (const interrupt of interrupts) {
      const metadata =
        interrupt.metadata && typeof interrupt.metadata === 'object'
          ? interrupt.metadata
          : {}
      const kind = typeof metadata.kind === 'string' ? metadata.kind : undefined
      const toolCallId = interrupt.toolCallId
      if (!toolCallId) continue

      const owner =
        interrupt.subagentRunId !== undefined
          ? this.childOwning(interrupt.subagentRunId)
          : this.childOwningToolCall((part) => part.id === toolCallId)
      if (owner !== undefined) {
        this.updateChild(owner, (child) =>
          child.handleInterrupts([interrupt], false),
        )
      }

      const toolName =
        typeof metadata.toolName === 'string'
          ? metadata.toolName
          : this.findToolCallName(toolCallId)
      const input = Object.hasOwn(metadata, 'input') ? metadata.input : {}

      if (kind === 'approval' || interrupt.reason === 'approval_required') {
        if (owner !== undefined) {
          if (emit) {
            this.events.onApprovalRequest?.({
              toolCallId,
              toolName,
              input,
              approvalId: interrupt.id,
            })
          }
          continue
        }
        // An interrupt terminal arrives after MESSAGES_SNAPSHOT, which may have
        // rebuilt the message list without the live active-message/tool-call
        // maps. Fall back to locating the assistant message that actually owns
        // the tool-call part so the approval UI (part.state) still updates.
        const resolvedMessageId =
          this.getActiveAssistantMessageId() ??
          this.toolCallToMessage.get(toolCallId) ??
          this.messages.find(
            (m) =>
              m.role === 'assistant' &&
              m.parts.some(
                (p) => p.type === 'tool-call' && p.id === toolCallId,
              ),
          )?.id
        if (resolvedMessageId) {
          this.messages = updateToolCallApproval(
            this.messages,
            resolvedMessageId,
            toolCallId,
            interrupt.id,
          )
          this.emitMessagesChange()
        }

        if (!emit) continue
        this.events.onApprovalRequest?.({
          toolCallId,
          toolName,
          input,
          approvalId: interrupt.id,
        })
        continue
      }

      if (kind === 'client_tool' || interrupt.reason === 'client_tool_input') {
        // Generic interrupts in the same batch decide `toolResume`. Do not
        // run client tools until that policy is `continue`.
        if (hasGeneric || !emit) continue
        this.toolCallsSentToClient.add(toolCallId)
        this.events.onToolCall?.({
          toolCallId,
          toolName,
          input,
        })
      }
    }
  }

  private findToolCallName(toolCallId: string): string {
    for (const state of this.messageStates.values()) {
      const toolCall = state.toolCalls.get(toolCallId)
      if (toolCall) return toolCall.name
    }
    return ''
  }

  /**
   * Handle RUN_ERROR event
   */
  private handleRunErrorEvent(
    chunk: Extract<StreamChunk, { type: 'RUN_ERROR' }>,
  ): void {
    this.hasError = true
    const runId =
      getChunkRunId(chunk) ??
      (this.activeRuns.size === 1 ? [...this.activeRuns][0] : undefined)
    const currentIds =
      runId !== undefined
        ? (this.callMessageIds.get(runId) ?? this.unassignedCallMessageIds)
        : this.unassignedCallMessageIds
    if (runId) {
      this.activeRuns.delete(runId)
    } else {
      this.activeRuns.clear()
    }
    // A known run whose messages were all removed gets no new error row.
    const discardedCall =
      runId !== undefined &&
      this.callMessageIds.has(runId) &&
      currentIds.size === 0
    const messageId = discardedCall
      ? undefined
      : this.ensureAssistantMessage(
          [...currentIds].at(-1) ?? generateMessageId(),
          false,
        ).messageId
    // Mark the messages of the failed call, so replay drops them.
    const ids = new Set(currentIds)
    if (messageId !== undefined) ids.add(messageId)
    for (const id of ids) {
      this.mergeMessageMetadata(
        id,
        withTanstackMetadata(chunk, {
          stopReason: tanstackMetadata(chunk)?.stopReason ?? 'error',
        }).metadata,
      )
    }
    if (this.pendingManualMessageId && ids.has(this.pendingManualMessageId)) {
      this.pendingManualMessageId = null
      this.legacyManualMessage = false
    }
    if (runId !== undefined) this.callMessageIds.delete(runId)
    for (const id of ids) this.unassignedCallMessageIds.delete(id)
    // Prefer spec field `message`; fall back to deprecated `error.message`.
    // If neither is set, the chunk still carries debug context (provider
    // error codes, request ids, etc.) — log it so the failure isn't silent.
    const errorMessage = chunk.message || 'An error occurred'
    if (!chunk.message) {
      console.error(
        '[StreamProcessor] RUN_ERROR with no message; original chunk:',
        chunk,
      )
    }

    if (messageId !== undefined && this.structuredMessageIds.has(messageId)) {
      this.flushStructuredOutputUpdate(messageId)
      this.messages = errorStructuredOutputPart(
        this.messages,
        messageId,
        errorMessage,
      )
      this.structuredMessageIds.delete(messageId)
      this.emitStructuredOutputChange(messageId, 'error')
      this.emitMessagesChange()
    }

    // Attach the provider's structured error body (`rawEvent`) and `code` to
    // the surfaced Error so consumers can recover the upstream detail that the
    // RUN_ERROR's `message` alone discards. Both are optional and added only
    // when present, keeping the Error backward compatible.
    this.events.onError?.(runErrorEventToError(chunk))
  }

  /**
   * Handle STEP_STARTED event (for thinking/reasoning content).
   *
   * Records the stepId so later REASONING_MESSAGE_CONTENT deltas accumulate
   * into their own ThinkingPart. Does not create a message — the message
   * is lazily created when the first REASONING_MESSAGE_CONTENT arrives.
   */
  private handleStepStartedEvent(
    chunk: Extract<StreamChunk, { type: 'STEP_STARTED' }>,
  ): void {
    const stepId = chunk.stepName || generateMessageId()
    const activeId = this.getActiveAssistantMessageId()
    if (activeId) {
      const state = this.getMessageState(activeId)
      if (state) {
        state.currentThinkingStepId = stepId
        if (!state.thinkingSteps.has(stepId)) {
          state.thinkingSteps.set(stepId, '')
          state.thinkingStepOrder.push(stepId)
        }
        // Clear any pending stepId from a prior STEP_STARTED that fired
        // before the assistant message existed. Now that we're tracking
        // the step directly on message state, the pending value is stale
        // and must not leak into the next REASONING_MESSAGE_CONTENT.
        this.pendingThinkingStepId = null
        return
      }
    }

    // No active message yet — defer until ensureAssistantMessage in
    // REASONING_MESSAGE_CONTENT
    this.pendingThinkingStepId = stepId
  }

  /**
   * Handle ACTIVITY_SNAPSHOT. Creates or replaces a frontend-only activity
   * message. Does not create MessageStreamState (that path feeds
   * ProcessorResult.content).
   */
  private handleActivitySnapshotEvent(
    chunk: Extract<StreamChunk, { type: 'ACTIVITY_SNAPSHOT' }>,
  ): void {
    this.messages = applyActivitySnapshotToUIMessages(this.messages, chunk)
    this.emitMessagesChange()
  }

  /**
   * Handle ACTIVITY_DELTA. Applies RFC 6902 patches to an existing activity
   * `content`. Missing or non-activity ids are no-ops. Patch failure leaves
   * the previous content in place.
   */
  private handleActivityDeltaEvent(
    chunk: Extract<StreamChunk, { type: 'ACTIVITY_DELTA' }>,
  ): void {
    this.messages = applyActivityDeltaToUIMessages(this.messages, chunk)
    this.emitMessagesChange()
  }

  /**
   * Handle STEP_FINISHED event.
   *
   * Thinking *content* comes from REASONING_MESSAGE_CONTENT, not STEP_FINISHED.
   * But some adapters (e.g. BytePlus thinking-summary) carry the provider
   * signature blob ONLY on the STEP_FINISHED event, so still extract that here
   * and attach it to the thinking step the reasoning events already built.
   */
  private handleStepFinishedEvent(
    chunk: Extract<StreamChunk, { type: 'STEP_FINISHED' }>,
  ): void {
    const extra = chunk as AdapterYieldChunk
    const signature = extra.signature
    if (!signature) return

    const { messageId, state } = this.ensureAssistantMessage(
      this.getActiveAssistantMessageId() ?? undefined,
    )
    const stepId = state.currentThinkingStepId ?? extra.stepId
    if (!stepId) return
    const thinking = state.thinkingSteps.get(stepId)
    if (thinking === undefined) return

    state.thinkingStepSignatures.set(stepId, signature)
    this.messages = updateThinkingPart(
      this.messages,
      messageId,
      stepId,
      thinking,
      signature,
    )
    this.emitMessagesChange()
  }

  /**
   * Handle REASONING_MESSAGE_CONTENT event (AG-UI reasoning protocol).
   *
   * Accumulates reasoning delta into thinking content and updates the
   * corresponding ThinkingPart in the UIMessage.
   */
  private handleReasoningMessageContentEvent(
    chunk: Extract<StreamChunk, { type: 'REASONING_MESSAGE_CONTENT' }>,
  ): void {
    const { messageId, state } = this.ensureAssistantMessage(
      this.getActiveAssistantMessageId() ?? undefined,
    )
    this.mergeMessageMetadata(messageId, chunk.metadata)

    state.hasSeenReasoningEvents = true
    const delta = chunk.delta || ''

    this.consumePendingThinkingStep(state)

    const stepId = state.currentThinkingStepId ?? chunk.messageId
    if (!state.thinkingSteps.has(stepId)) {
      state.thinkingSteps.set(stepId, '')
      state.thinkingStepOrder.push(stepId)
      state.currentThinkingStepId = stepId
    }

    const nextThinking = (state.thinkingSteps.get(stepId) ?? '') + delta
    state.thinkingSteps.set(stepId, nextThinking)

    this.messages = updateThinkingPart(
      this.messages,
      messageId,
      stepId,
      nextThinking,
      state.thinkingStepSignatures.get(stepId),
    )
    this.emitMessagesChange()

    this.events.onThinkingUpdate?.(messageId, stepId, nextThinking)
  }

  /**
   * Attach a provider signature blob from REASONING_ENCRYPTED_VALUE.
   * `subtype: 'message'` updates ThinkingPart.signature.
   * `subtype: 'tool-call'` stores Gemini thoughtSignature on the tool-call part.
   */
  private handleReasoningEncryptedValueEvent(
    chunk: Extract<StreamChunk, { type: 'REASONING_ENCRYPTED_VALUE' }>,
  ): void {
    const encryptedValue = chunk.encryptedValue
    if (typeof encryptedValue !== 'string' || encryptedValue === '') return

    if (chunk.subtype === 'tool-call') {
      this.attachToolCallSignature(chunk.entityId, encryptedValue)
      return
    }

    const { messageId, state } = this.ensureAssistantMessage(
      this.getActiveAssistantMessageId() ?? undefined,
    )
    const stepId = state.currentThinkingStepId ?? chunk.entityId
    this.mergeMessageMetadata(messageId, chunk.metadata)
    state.thinkingStepSignatures.set(stepId, encryptedValue)
    const content = state.thinkingSteps.get(stepId) ?? ''
    if (!state.thinkingSteps.has(stepId)) {
      state.thinkingSteps.set(stepId, content)
      state.thinkingStepOrder.push(stepId)
    }
    this.messages = updateThinkingPart(
      this.messages,
      messageId,
      stepId,
      content,
      encryptedValue,
    )
    this.emitMessagesChange()
  }

  private attachToolCallSignature(
    toolCallId: string,
    thoughtSignature: string,
  ): void {
    this.messages = this.messages.map((msg) => {
      let changed = false
      const parts = msg.parts.map((part) => {
        if (part.type !== 'tool-call' || part.id !== toolCallId) return part
        changed = true
        return {
          ...part,
          metadata: {
            ...(part.metadata != null && typeof part.metadata === 'object'
              ? part.metadata
              : {}),
            thoughtSignature,
          },
        }
      })
      return changed ? { ...msg, parts } : msg
    })
    for (const state of this.messageStates.values()) {
      const call = state.toolCalls.get(toolCallId)
      if (!call) continue
      call.metadata = {
        ...(call.metadata ?? {}),
        thoughtSignature,
      }
    }
    this.emitMessagesChange()
  }

  /**
   * Handle CUSTOM event.
   *
   * Handles custom events consumed by the processor:
   * - 'tool-input-available': Legacy/replay-compatible input for client tool
   *   execution. Fires onToolCall.
   * - 'approval-requested': Legacy/replay-compatible input for tool approval.
   *   Updates tool-call part state and fires onApprovalRequest.
   *
   * Current core streams represent user-actionable waits through
   * RUN_FINISHED.outcome.type === 'interrupt'; these custom events are not the
   * source of truth for new emissions.
   *
   * @see docs/chat-architecture.md#client-tools-and-approval-flows — Full flow details
   */
  private handleCustomEvent(
    chunk: Extract<StreamChunk, { type: 'CUSTOM' }>,
  ): void {
    const messageId = this.getActiveAssistantMessageId()

    if (chunk.name === 'structured-output.start' && chunk.value) {
      const v = chunk.value as { messageId?: string }
      const { messageId: targetId } = this.ensureAssistantMessage(
        v.messageId && this.getMessageState(v.messageId)
          ? v.messageId
          : (messageId ?? v.messageId),
      )
      if (targetId) {
        this.structuredMessageIds.add(targetId)
        this.structuredOutputUpdateBatches.delete(targetId)
        this.events.onStructuredOutputChange?.({
          phase: 'start',
          messageId: targetId,
          status: 'streaming',
          raw: '',
        })
      }
      return
    }

    if (chunk.name === 'structured-output.complete' && chunk.value) {
      const v = chunk.value as {
        object: unknown
        raw?: string
        reasoning?: string
        messageId?: string
      }
      const { messageId: targetId } = this.ensureAssistantMessage(
        v.messageId && this.getMessageState(v.messageId)
          ? v.messageId
          : (messageId ?? v.messageId),
      )
      if (targetId) {
        this.flushStructuredOutputUpdate(targetId)
        this.messages = completeStructuredOutputPart(
          this.messages,
          targetId,
          v.object,
          v.raw ?? '',
          v.reasoning,
        )
        this.structuredMessageIds.delete(targetId)
        this.emitStructuredOutputChange(targetId, 'complete')
        this.emitMessagesChange()
      }
      // Fall through so user `onCustomEvent` callbacks still observe the event.
    }

    // Handle client tool input availability - trigger client-side execution
    if (chunk.name === 'tool-input-available' && chunk.value) {
      const { toolCallId, toolName, input } = chunk.value as {
        toolCallId: string
        toolName: string
        input: any
      }

      // Emit onToolCall event for the client to execute the tool
      this.toolCallsSentToClient.add(toolCallId)
      this.events.onToolCall?.({
        toolCallId,
        toolName,
        input,
      })
      return
    }

    // Handle approval requests
    if (chunk.name === 'approval-requested' && chunk.value) {
      const { toolCallId, toolName, input, approval } = chunk.value as {
        toolCallId: string
        toolName: string
        input: any
        approval: { id: string; needsApproval: boolean }
      }

      // Resolve the message containing this tool call. After RUN_FINISHED,
      // activeMessageIds is cleared, so fall back to the toolCallToMessage map
      // which is populated during TOOL_CALL_START and preserved across finalize.
      const resolvedMessageId =
        messageId ?? this.toolCallToMessage.get(toolCallId)
      if (resolvedMessageId) {
        this.messages = updateToolCallApproval(
          this.messages,
          resolvedMessageId,
          toolCallId,
          approval.id,
        )
        this.emitMessagesChange()
      }

      // Emit approval request event
      this.events.onApprovalRequest?.({
        toolCallId,
        toolName,
        input,
        approvalId: approval.id,
      })
      return
    }

    // Handle MCP Apps ui-resource events — materialize a UIResourcePart on the
    // active assistant message. Never falls through to onCustomEvent because
    // ui-resource is a system event, not a user-defined custom event.
    if (chunk.name === 'ui-resource' && chunk.value) {
      const v: UIResourceEvent['value'] = chunk.value
      // Resolve the target assistant message. When a toolCallId is present, the
      // tool call's OWNER message is authoritative, so prefer it first; fall
      // back to the active assistant id only if the tool call isn't mapped.
      // This avoids misattaching the widget to a different active message in a
      // multi-message session.
      const resolvedMessageId =
        this.toolCallToMessage.get(v.toolCallId) ?? messageId
      if (resolvedMessageId) {
        const part: UIResourcePart = {
          type: 'ui-resource',
          resource: v.resource,
          toolCallId: v.toolCallId,
          toolName: v.toolName,
          ...(v.serverId !== undefined && { serverId: v.serverId }),
          ...(v.meta !== undefined && { meta: v.meta }),
        }
        this.messages = this.messages.map((msg) =>
          msg.id === resolvedMessageId
            ? { ...msg, parts: [...msg.parts, part] }
            : msg,
        )
        this.emitMessagesChange()
      } else {
        // No owner message and no active assistant id — the server read and
        // streamed a widget that has nowhere to attach (e.g. a toolCallId never
        // registered, or the event arrived after the run cleared its active
        // ids). Drop fail-soft, but warn: a vanished widget is otherwise
        // undebuggable from the client.
        console.warn(
          `[mcp-apps] dropped ui-resource: no target message for toolCallId "${v.toolCallId}" (toolName "${v.toolName}")`,
        )
      }
      return
    }

    // Forward non-system custom events to onCustomEvent callback
    if (this.events.onCustomEvent) {
      const toolCallId =
        chunk.value && typeof chunk.value === 'object'
          ? chunk.value.toolCallId
          : undefined
      this.events.onCustomEvent(chunk.name, chunk.value, { toolCallId })
    }
  }

  // ============================================
  // Internal Helpers
  // ============================================

  /**
   * Detect if an incoming content chunk represents a NEW text segment
   */
  private isNewTextSegment(
    _chunk: Extract<StreamChunk, { type: 'TEXT_MESSAGE_CONTENT' }>,
    _previous: string,
  ): boolean {
    return true
  }

  /**
   * Complete all tool calls across all active messages — safety net for stream termination.
   *
   * Called by RUN_FINISHED and finalizeStream(). Force-transitions any tool call
   * not yet in input-complete state. Handles cases where TOOL_CALL_END was
   * missed (adapter bug, network error, aborted stream).
   *
   * @see docs/chat-architecture.md#single-shot-tool-call-response — Safety net behavior
   */
  private completeAllToolCalls(): void {
    for (const messageId of this.activeMessageIds) {
      this.completeAllToolCallsForMessage(messageId)
    }
  }

  /**
   * Complete all tool calls for a specific message
   */
  private completeAllToolCallsForMessage(messageId: string): void {
    const state = this.getMessageState(messageId)
    if (!state) return

    state.toolCalls.forEach((toolCall, id) => {
      if (toolCall.state !== 'input-complete') {
        const index = state.toolCallOrder.indexOf(id)
        this.completeToolCall(messageId, index, toolCall)
      }
    })
  }

  /**
   * Mark a tool call as complete and emit event
   */
  private completeToolCall(
    messageId: string,
    _index: number,
    toolCall: InternalToolCallState,
  ): void {
    // Finalize the internal bookkeeping: the call's input arguments ARE
    // complete regardless of whether execution later failed, so the call still
    // counts as a completed tool call in getCompletedToolCalls()/getState().
    toolCall.state = 'input-complete'

    // Only surface `input` from a strict parse. The streaming partial-JSON
    // parser closes unterminated strings, so truncated arguments would become
    // a plausible but wrong object (GitHub issue #1017). If parse fails,
    // `input` stays unset and consumers use the raw `arguments` string.
    let strictParseSucceeded = false
    try {
      toolCall.parsedArguments = JSON.parse(toolCall.arguments)
      strictParseSucceeded = true
    } catch {
      toolCall.parsedArguments = undefined
    }

    // Don't downgrade the rendered part of a call that already reached a
    // terminal 'error' or 'complete' state (e.g. a tool result arrived
    // without a preceding TOOL_CALL_END). The RUN_FINISHED / finalizeStream
    // safety net must not clobber a finished call back to 'input-complete'.
    if (this.isToolCallPartTerminal(toolCall.id)) {
      return
    }

    // RUN_FINISHED interrupt handling can mark the rendered part as waiting on
    // user action before the completion safety net runs. Keep the parsed
    // argument bookkeeping above, but do not downgrade that visible state.
    if (this.isToolCallPartAwaitingUserAction(toolCall.id)) {
      return
    }

    // Update UIMessage. The arguments are complete now, so surface the parsed
    // input on the part from the accumulated TOOL_CALL_ARGS deltas.
    this.messages = updateToolCallPart(this.messages, messageId, {
      id: toolCall.id,
      name: toolCall.name,
      arguments: toolCall.arguments,
      state: 'input-complete',
      ...(strictParseSucceeded && { input: toolCall.parsedArguments }),
      ...(toolCall.metadata !== undefined && { metadata: toolCall.metadata }),
    })
    this.emitMessagesChange()

    // Emit granular event
    this.events.onToolCallStateChange?.(
      messageId,
      toolCall.id,
      'input-complete',
      toolCall.arguments,
    )
  }

  private isToolCallPartAwaitingUserAction(toolCallId: string): boolean {
    return this.messages.some((msg) =>
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- `parts` is typed as required, but seeded ModelMessage-shaped messages can lack it at runtime.
      msg.parts?.some(
        (part) =>
          part.type === 'tool-call' &&
          part.id === toolCallId &&
          (part.state === 'approval-requested' ||
            part.state === 'approval-responded'),
      ),
    )
  }

  /**
   * Whether the rendered tool-call part for the given id has reached a
   * terminal 'error' or 'complete' state. Used to prevent the completion
   * safety net from downgrading a finished call back to 'input-complete'.
   */
  private isToolCallPartTerminal(toolCallId: string): boolean {
    // `initialMessages` may be ModelMessage-shaped (no `parts`) — e.g. the
    // common pattern of seeding a processor with the same messages passed to
    // `chat()`. Guard the access so iterating them never throws.
    return this.messages.some((msg) =>
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- `parts` is typed as required, but seeded ModelMessage-shaped messages can lack it at runtime.
      msg.parts?.some(
        (part) =>
          part.type === 'tool-call' &&
          part.id === toolCallId &&
          (part.state === 'error' || part.state === 'complete'),
      ),
    )
  }

  /**
   * Emit pending text update for a specific message.
   *
   * Calls updateTextPart() which has critical append-vs-replace logic:
   * - If last UIMessage part is TextPart → replaces its content (same segment).
   * - If last part is anything else → pushes new TextPart (new segment after tools).
   *
   * @see docs/chat-architecture.md#uimessage-part-ordering-invariants — Replace vs. push logic
   */
  private emitTextUpdateForMessage(messageId: string): void {
    const state = this.getMessageState(messageId)
    if (!state) return

    state.lastEmittedText = state.currentSegmentText

    // Update UIMessage
    this.messages = updateTextPart(
      this.messages,
      messageId,
      state.currentSegmentText,
    )
    this.emitMessagesChange()

    // Emit granular event
    this.events.onTextUpdate?.(messageId, state.currentSegmentText)
  }

  private queueStructuredOutputUpdate(messageId: string, delta: string): void {
    const existing = this.structuredOutputUpdateBatches.get(messageId)
    const next = {
      delta: `${existing?.delta ?? ''}${delta}`,
      chunkCount: (existing?.chunkCount ?? 0) + 1,
    }

    this.structuredOutputUpdateBatches.set(messageId, next)

    if (next.chunkCount >= STRUCTURED_OUTPUT_UPDATE_BATCH_SIZE) {
      this.flushStructuredOutputUpdate(messageId)
    }
  }

  private flushStructuredOutputUpdate(messageId: string): void {
    const batch = this.structuredOutputUpdateBatches.get(messageId)
    if (!batch || batch.chunkCount === 0) return

    this.structuredOutputUpdateBatches.delete(messageId)
    this.emitStructuredOutputChange(messageId, 'update', batch.delta)
  }

  private emitStructuredOutputChange(
    messageId: string,
    phase: 'update' | 'complete' | 'error',
    delta?: string,
  ): void {
    const part = this.messages
      .find((message) => message.id === messageId)
      ?.parts.find(
        (
          messagePart,
        ): messagePart is Extract<MessagePart, { type: 'structured-output' }> =>
          messagePart.type === 'structured-output',
      )
    if (!part) return

    this.events.onStructuredOutputChange?.({
      phase,
      messageId,
      status: part.status,
      raw: part.raw,
      ...(part.partial !== undefined ? { partial: part.partial } : {}),
      ...(part.data !== undefined ? { data: part.data } : {}),
      ...(part.reasoning !== undefined ? { reasoning: part.reasoning } : {}),
      ...(part.errorMessage !== undefined
        ? { errorMessage: part.errorMessage }
        : {}),
      ...(delta !== undefined ? { delta } : {}),
    })
  }

  /**
   * Emit messages change event
   */
  private emitMessagesChange(): void {
    this.events.onMessagesChange?.([...this.messages])
  }

  /**
   * Finalize the stream — complete all pending operations.
   *
   * Called when the async iterable ends (stream closed). Acts as the final
   * safety net: completes any remaining tool calls, flushes un-emitted text,
   * and fires onStreamEnd.
   *
   * @see docs/chat-architecture.md#single-shot-text-response — Finalization step
   */
  finalizeStream(): void {
    this.isDone = true
    for (const event of this.closeChunk()) this.dispatchChunk(event)
    this.closeChildChunkLanes()
    let lastAssistantMessage: UIMessage | undefined

    // Finalize ALL active messages
    for (const messageId of this.activeMessageIds) {
      const state = this.getMessageState(messageId)
      if (!state) continue

      // Complete any remaining tool calls
      this.completeAllToolCallsForMessage(messageId)

      // Emit any pending text if not already emitted
      if (state.currentSegmentText !== state.lastEmittedText) {
        this.emitTextUpdateForMessage(messageId)
      }

      state.isComplete = true

      const msg = this.messages.find((m) => m.id === messageId)
      if (msg && msg.role === 'assistant') {
        lastAssistantMessage = msg
      }
    }

    // The stream closed but one or more structured-output runs never sent
    // their terminal `structured-output.complete`. Snap each lingering
    // streaming part to error so the UI doesn't appear to stream forever,
    // and drop the routing entries so a subsequent run on the same
    // processor instance (long-lived `subscribe()` mode) doesn't reuse
    // the stale ids.
    //
    // The iteration is unconditional w.r.t. `this.hasError` — RUN_ERROR
    // already removed its target messageId from `structuredMessageIds`
    // before reaching finalize, so anything still in the set is by
    // definition a non-errored, never-completed run (the multi-run case:
    // run-A errors, run-B is still streaming when finalize fires).
    for (const messageId of this.structuredMessageIds) {
      this.flushStructuredOutputUpdate(messageId)
      this.messages = errorStructuredOutputPart(
        this.messages,
        messageId,
        'Stream ended without structured-output.complete',
      )
      this.emitStructuredOutputChange(messageId, 'error')
    }
    this.structuredMessageIds.clear()
    this.structuredOutputUpdateBatches.clear()

    this.activeMessageIds.clear()

    // Remove whitespace-only assistant messages (handles models like Gemini
    // that sometimes return just "\n" during auto-continuation).
    // Preserve the message on errors so the UI can show error state.
    if (lastAssistantMessage && !this.hasError) {
      if (this.isWhitespaceOnlyMessage(lastAssistantMessage)) {
        this.messages = this.messages.filter(
          (m) => m.id !== lastAssistantMessage.id,
        )
        this.emitMessagesChange()
        return
      }
    }

    // Emit stream end for the last assistant message
    if (lastAssistantMessage && !this.streamEndEmitted) {
      this.streamEndEmitted = true
      this.events.onStreamEnd?.(lastAssistantMessage)
    }
  }

  /**
   * Get completed tool calls in API format (aggregated across all messages)
   */
  private getCompletedToolCalls(): Array<ToolCall> {
    const result: Array<ToolCall> = []
    for (const state of this.messageStates.values()) {
      for (const tc of state.toolCalls.values()) {
        if (tc.state === 'input-complete') {
          result.push({
            id: tc.id,
            type: 'function' as const,
            function: {
              name: tc.name,
              arguments: tc.arguments,
            },
            // Preserve provider metadata (e.g. Gemini thoughtSignature) on
            // ProcessorResult.toolCalls so callers using process()/getResult()
            // get the same round-trip support as the streaming UI path.
            ...(tc.metadata !== undefined && { metadata: tc.metadata }),
          })
        }
      }
    }
    return result
  }

  /**
   * Get current result (aggregated across all messages)
   */
  private getResult(): ProcessorResult {
    const toolCalls = this.getCompletedToolCalls()
    let content = ''
    let thinking = ''

    for (const state of this.messageStates.values()) {
      content += state.totalTextContent
      for (const stepId of state.thinkingStepOrder) {
        thinking += state.thinkingSteps.get(stepId) ?? ''
      }
    }

    return {
      content,
      thinking: thinking || undefined,
      toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
      finishReason: this.finishReason,
    }
  }

  /**
   * Get current processor state (aggregated across all messages)
   */
  getState(): ProcessorState {
    let content = ''
    let thinking = ''
    const toolCalls = new Map<string, InternalToolCallState>()
    const toolCallOrder: Array<string> = []

    for (const state of this.messageStates.values()) {
      content += state.totalTextContent
      for (const stepId of state.thinkingStepOrder) {
        thinking += state.thinkingSteps.get(stepId) ?? ''
      }
      for (const [id, tc] of state.toolCalls) {
        toolCalls.set(id, tc)
      }
      toolCallOrder.push(...state.toolCallOrder)
    }

    return {
      content,
      thinking,
      toolCalls,
      toolCallOrder,
      finishReason: this.finishReason,
      done: this.isDone,
    }
  }

  /**
   * Start recording chunks
   */
  startRecording(): void {
    this.recordingEnabled = true
    this.recordingStartTime = Date.now()
    this.recording = {
      version: '1.0',
      timestamp: this.recordingStartTime,
      chunks: [],
    }
  }

  /**
   * Get the current recording
   */
  getRecording(): ChunkRecording | null {
    return this.recording
  }

  /**
   * Reset stream state (but keep messages)
   */
  private resetStreamState(): void {
    this.messageStates.clear()
    this.callMessageIds.clear()
    this.unassignedCallMessageIds.clear()
    this.activeMessageIds.clear()
    this.activeRuns.clear()
    this.toolCallToMessage.clear()
    this.structuredMessageIds.clear()
    this.structuredOutputUpdateBatches.clear()
    this.pendingManualMessageId = null
    this.legacyManualMessage = false
    this.pendingThinkingStepId = null
    this.openChunk = null
    this.finishReason = null
    this.hasError = false
    this.isDone = false
    this.streamEndEmitted = false
    this.chunkStrategy.reset?.()
  }

  /**
   * Full reset (including messages)
   */
  reset(): void {
    this.resetStreamState()
    this.messages = []
    this.childProcessors.clear()
    this.childToolCalls.clear()
  }

  /**
   * Check if a message contains only whitespace text and no other meaningful parts
   * (no tool calls, tool results, thinking, etc.)
   */
  private isWhitespaceOnlyMessage(message: UIMessage): boolean {
    if (message.parts.length === 0) return false
    return message.parts.every(
      (part) => part.type === 'text' && part.content.trim() === '',
    )
  }

  /**
   * Replay a recording through the processor
   */
  static async replay(
    recording: ChunkRecording,
    options?: StreamProcessorOptions,
  ): Promise<ProcessorResult> {
    const processor = new StreamProcessor(options)
    return processor.process(createReplayStream(recording))
  }
}

/**
 * Create an async iterable from a recording
 */
export function createReplayStream(
  recording: ChunkRecording,
): AsyncIterable<StreamChunk> {
  return {
    // eslint-disable-next-line @typescript-eslint/require-await -- async generator required by AsyncIterable contract; body has no await
    async *[Symbol.asyncIterator]() {
      for (const { chunk } of recording.chunks) {
        yield chunk
      }
    },
  }
}
